import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Drawer,
  FormControlLabel, IconButton, Menu, MenuItem, Snackbar, Stack, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography, useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TranslateIcon from '@mui/icons-material/Translate';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import FrontHandOutlinedIcon from '@mui/icons-material/FrontHandOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import { buildPdfPayloadFromTemplate } from '../office/pdfPayloadBuilder';
import { applyTemplateTokens } from '../office/templateTokens';
import {
  allRows, applyEnglishEdit, applyProposal, agencyKey, approvedMaterials, groupAgencies, SOP_GROUPS, BLANK_VALUE, buildSopPdfBody, checklistStatus, COMMON_SOP_ID, draftMark, draftSummary,
  formatPlaceholderValue, guReadiness, guState, hasGujaratiScript, insertAfter, newPointId, pdfPlaceholderKeys, pdfRows,
  placeholderByKey, placeholderReport, publishBlockers, pushLog, sampleFor, sampleValues, sectionRows, shortText, splitPlaceholders,
  SOP_GLOSSARY, SOP_IMPACTS, SOP_KEEP_AS_IS, SOP_LABELS_GU, SOP_PLACEHOLDERS, SOP_POINT_TYPES, SOP_SECTIONS, SOP_SECTIONS_GU, workingClauses,
  type PlaceholderIssueKind, type SopAgency, type SopCheckAnswer, type SopCheckItem, type SopImpact, type SopLesson, type SopLessonStatus, type SopPoint, type SopProposal,
  type SopRateRow, type SopRow, type SopSample,
} from '../office/sops';
import {
  createAgency, createLesson, discardDraft, draftWithAi, editDraft, formalizeLesson, generateSopPdf, lessonToEnglish,
  loadChecklistAnswers, loadSopPdfContext, publishDraft, rewriteInSopStyle, saveChecklistAnswer, saveReview, useSopChecklists, seedStarterSops, translateErrorMessage, translateTexts, updateAgency, updateLesson,
  useSopAgencies, useSopLessons, useSopPdfs, type SopPdfRecord,
} from '../office/sopStore';

const BORDER = '#e8e8e8';
const GOLD = '#9d6f32';
const GOLD_D = '#7a5525';
const GOLD_SOFT = '#f6ecdb';
const MUTED = '#6b6b6b';
const FAINT = '#938b7d';
const DARK = '#1a1a1a';
const HOLD = '#b3432b';
const HOLD_SOFT = '#fbeae4';
const GREEN = '#3c6b4b';
const GREEN_SOFT = '#e5f2ea';
const BLUE = '#2f5f96';
const BLUE_SOFT = '#eef3fb';
const WARN = '#a8640c';
const WARN_SOFT = '#fbf0dc';
const SUNK = '#f5f3ef';
const GU_FONT = "'Noto Sans Gujarati', 'Shruti', 'Inter', sans-serif";
const HEAD_FONT = "Georgia, 'Times New Roman', serif";

type Lang = 'en' | 'gu' | 'both';
type Editing = { kind: 'en' | 'gu'; agencyId: string; pointId: string } | null;
type AddPre = { targets?: string[]; section?: number; after?: string; en?: string; gu?: string; hold?: boolean; lessonId?: string };
type Snack = { message: string; undo?: () => Promise<void> } | null;
type ProjectRef = { id: string; name: string };

const guText = (x: Pick<SopPoint, 'gu'>) => (x.gu && x.gu.text ? x.gu.text : '');
const tagSx = (bg: string, color: string, border?: string) => ({
  display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: 11, fontWeight: 700, lineHeight: '18px', px: 0.8, borderRadius: 1,
  bgcolor: bg, color, border: border ? `1px solid ${border}` : 'none', whiteSpace: 'nowrap',
});

export default function OfficeSopsPage() {
  const { currentUser, currentProjectId, roleId } = useAuth();
  const { canAction } = usePermissions();
  const isAdmin = ['super-admin', 'admin'].includes(String(roleId || ''));
  const canManage = isAdmin || canAction('sops', 'manage');
  const byName = currentUser?.displayName || currentUser?.email || 'Someone';
  const wide = useMediaQuery('(min-width:900px)');

  const { agencies, loading, error: loadError } = useSopAgencies();
  const lessons = useSopLessons();
  const [projects, setProjects] = useState<ProjectRef[]>([]);
  useEffect(() => onSnapshot(collection(db, 'projects'), (snap) => {
    setProjects(snap.docs.map((d) => ({ id: d.id, name: String((d.data() as any)?.name || d.id) })).sort((a, b) => a.name.localeCompare(b.name)));
  }, () => setProjects([])), []);

  const [agencyId, setAgencyId] = useState('');
  const [lang, setLang] = useState<Lang>('en');
  const [tab, setTab] = useState<'sop' | 'pending' | 'review' | 'placeholders' | 'checklist'>('sop');
  const [checkProjectId, setCheckProjectId] = useState(currentProjectId || '');
  const [editingChecklist, setEditingChecklist] = useState(false);
  const checklists = useSopChecklists(checkProjectId);
  const [showApproved, setShowApproved] = useState(false);
  const [preview, setPreview] = useState(false);
  const [lessonFilter, setLessonFilter] = useState<'new' | 'done' | 'all'>('new');
  const [editing, setEditing] = useState<Editing>(null);
  const [editText, setEditText] = useState('');
  const [editHold, setEditHold] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [commonOpen, setCommonOpen] = useState(false);
  const [closedGroups, setClosedGroups] = useState<string[]>(() => {
    try { const v = JSON.parse(localStorage.getItem('sopClosedGroups') || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
  });
  const [drawer, setDrawer] = useState<{ kind: 'point'; pre: AddPre } | { kind: 'agency' } | { kind: 'lesson' } | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [snack, setSnack] = useState<Snack>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [flash, setFlash] = useState<string[]>([]);

  const common = agencies.find((a) => a.id === COMMON_SOP_ID) || null;
  const active = agencies.filter((a) => a.id !== COMMON_SOP_ID && !a.archived);
  // Site team lists (Project set-up) are not agencies: never offered when adding points.
  const agencyChoices = [...(agencies.filter((a) => a.id === COMMON_SOP_ID)), ...active.filter((a) => !a.siteTeam)];
  const archived = agencies.filter((a) => a.id !== COMMON_SOP_ID && a.archived);
  const current = agencies.find((a) => a.id === agencyId && !a.archived) || active[0] || common;
  const newCount = (aid: string) => lessons.filter((l) => l.agencyId === aid && l.status === 'new').length;
  const pdfs = useSopPdfs(current?.id);

  useEffect(() => { if (!agencyId && current) setAgencyId(current.id); }, [agencyId, current]);
  useEffect(() => { setShowApproved(false); setEditingChecklist(false); }, [agencyId]);
  useEffect(() => { if (!checkProjectId && (currentProjectId || projects[0]?.id)) setCheckProjectId(currentProjectId || projects[0].id); }, [checkProjectId, currentProjectId, projects]);
  useEffect(() => { if (flash.length) { const t = setTimeout(() => setFlash([]), 1800); return () => clearTimeout(t); } return undefined; }, [flash]);

  const closeEdit = () => { setEditing(null); setEditText(''); setEditHold(false); };
  const run = async (fn: () => Promise<unknown>, message?: string, undo?: () => Promise<void>) => {
    setError('');
    try { await fn(); if (message) setSnack({ message, undo }); return true; } catch (err: any) { setError(err?.message || 'Could not save. Try again.'); return false; }
  };
  const withBusy = async (key: string, fn: () => Promise<void>) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try { await fn(); } catch (err: any) { setError(translateErrorMessage(err)); } finally { setBusy((b) => { const n = { ...b }; delete n[key]; return n; }); }
  };

  // ---------- Writes ----------
  const bumped = (a: SopAgency, note: string): SopAgency => ({ ...a, version: a.version + 1, log: pushLog(a.log, { v: a.version + 1, note, at: new Date().toISOString(), by: byName }) });

  const addPoint = async (input: AddPointInput) => {
    const ids: string[] = [];
    const ok = await run(async () => {
      for (const tid of input.targets) {
        const point: SopPoint = { id: newPointId(), section: input.section, text: input.en, hold: input.hold };
        if (input.gu) point.gu = { text: input.gu, state: input.guok ? 'ok' : 'ai' };
        ids.push(point.id);
        await editDraft(tid, (clauses) => {
          const after = input.after && clauses.some((x) => x.id === input.after) ? input.after : undefined;
          return insertAfter(clauses, point, after);
        }, `Added: ${shortText(input.en)}`, byName);
      }
      if (input.lessonId) {
        const names = input.targets.map((t) => agencies.find((a) => a.id === t)?.name || t).join(', ');
        await updateLesson(input.lessonId, { status: 'added', result: `Added to the ${names} draft` });
      }
    }, input.targets.length > 1 ? `Added to ${input.targets.length} drafts` : 'Added to the draft');
    if (!ok) return false;
    if (input.targets.length === 1) setAgencyId(input.targets[0]);
    if (input.targets.includes(COMMON_SOP_ID) && agencyId !== COMMON_SOP_ID) setCommonOpen(true);
    setShowApproved(false);
    setTab('sop');
    setFlash(ids);
    return true;
  };

  const saveEnglish = (a: SopAgency, pointId: string) => {
    const text = editText.trim();
    if (!text) return;
    const hold = editHold;
    const before = workingClauses(a).find((p) => p.id === pointId);
    const guStale = !!before && before.text !== text && !!guText(before);
    run(() => editDraft(a.id, (clauses) => clauses.map((p) => (p.id === pointId ? applyEnglishEdit(p, text, hold) : p)), `Changed: ${shortText(text)}`, byName),
      guStale ? 'Saved in the draft. Gujarati marked to check' : 'Saved in the draft').then((ok) => { if (ok) { setFlash([pointId]); closeEdit(); } });
  };

  const removePoint = (a: SopAgency, pointId: string) => {
    const list = workingClauses(a);
    const point = list.find((p) => p.id === pointId);
    if (!point) return;
    const index = list.findIndex((p) => p.id === pointId);
    run(() => editDraft(a.id, (clauses) => clauses.filter((p) => p.id !== pointId), `Removed: ${shortText(point.text)}`, byName),
      'Removed from the draft',
      async () => { await editDraft(a.id, (clauses) => { const next = [...clauses]; next.splice(Math.min(index, next.length), 0, point); return next; }, `Restored: ${shortText(point.text)}`, byName); });
  };

  const saveGujarati = (a: SopAgency, row: SopRow) => {
    const gu = editText.trim();
    run(() => editDraft(a.id, (clauses) => clauses.map((p) => {
      if (p.id !== row.id) return p;
      const next = { ...p };
      if (gu) next.gu = { text: gu, state: 'ok' }; else delete next.gu;
      return next;
    }), `Gujarati checked: ${shortText(row.text, 44)}`, byName), 'Gujarati saved as checked').then((ok) => { if (ok) { setFlash([row.id]); closeEdit(); } });
  };

  /** AI-draft every missing Gujarati in this SOP's draft. Common points are translated on the Common page. */
  const translateMissing = (a: SopAgency) => withBusy('batch-' + a.id, async () => {
    const jobs = workingClauses(a).filter((p) => guState(p) === 'missing');
    if (!jobs.length) return;
    const out = await translateTexts(jobs.map((p) => p.text), 'gu');
    const byId: Record<string, string> = {};
    jobs.forEach((p, i) => { byId[p.id] = out[i]; });
    await editDraft(a.id, (clauses) => clauses.map((p) => (byId[p.id] && !guText(p) ? { ...p, gu: { text: byId[p.id], state: 'ai' } } : p)), `AI Gujarati for ${jobs.length} points`, byName);
    setSnack({ message: `${jobs.length} points translated. Check each AI draft` });
  });

  const publish = (a: SopAgency) => run(async () => {
    const v = await publishDraft(a.id, byName);
    setSnack({ message: `Published v${v}. New PDFs use v${v}` });
  }).then((ok) => { if (ok) setShowApproved(false); });

  const discard = (a: SopAgency) => {
    const draft = a.draft;
    run(() => discardDraft(a.id, byName), 'Draft discarded',
      draft ? async () => { await updateAgency(a.id, (x) => ({ ...x, draft }), byName); } : undefined);
  };

  /** AI reads every pending point for this agency and proposes changes to its draft. */
  const draftAi = (a: SopAgency) => withBusy('draft-' + a.id, async () => {
    const pending = lessons.filter((l) => l.agencyId === a.id && l.status === 'new');
    if (!pending.length) return;
    const proposals = await draftWithAi(a.id === COMMON_SOP_ID ? 'Common (all agencies)' : a.name, workingClauses(a), a.id === COMMON_SOP_ID || !common ? [] : common.clauses, pending);
    await saveReview(a.id, proposals, byName);
    setTab('review');
    setSnack({ message: `${proposals.length} proposals ready to review` });
  });

  const decideProposal = async (a: SopAgency, proposal: SopProposal, accept: boolean, guChecked: boolean) => {
    const sources = lessons.filter((l) => proposal.sourceIds.includes(l.id));
    const targetRow = proposal.targetId ? [...allRows({ ...a, clauses: workingClauses(a) }, null), ...(common ? allRows(common, null) : [])].find((r) => r.id === proposal.targetId) : undefined;
    await run(async () => {
      if (accept && (proposal.kind === 'new' || proposal.kind === 'reword')) {
        await editDraft(a.id, (clauses) => applyProposal(clauses, proposal, guChecked), `${proposal.kind === 'reword' ? 'Improved' : 'Added'} from pending: ${shortText(proposal.en)}`, byName);
      }
      if (accept) {
        const status: SopLessonStatus = proposal.kind === 'duplicate' ? 'duplicate' : proposal.kind === 'vendor' ? 'vendor' : 'added';
        const result = proposal.kind === 'duplicate' ? `Already covered by point ${targetRow?.num || ''}`.trim()
          : proposal.kind === 'vendor' ? 'Kept as a vendor note' : `In draft v${a.version + 1}`;
        for (const l of sources) await updateLesson(l.id, { status, result });
      }
      await updateAgency(a.id, (x) => (x.review ? { ...x, review: { ...x.review, proposals: x.review.proposals.map((p) => (p.id === proposal.id ? { ...proposal, status: accept ? 'accepted' : 'rejected' } : p)) } } : x), byName);
    }, accept ? 'Accepted' : 'Rejected. The pending points stay pending');
  };

  const markLesson = (l: SopLesson, status: SopLessonStatus) => run(() => updateLesson(l.id, { status }), status === 'vendor' ? 'Kept as a vendor note' : status === 'duplicate' ? 'Marked as already covered' : 'Marked not needed',
    async () => { await updateLesson(l.id, { status: 'new' }); });
  const lessonEnglish = (l: SopLesson) => withBusy('en-' + l.id, async () => {
    const en = await lessonToEnglish(`${l.what}${l.suggest ? `\nSuggestion: ${l.suggest}` : ''}`);
    await updateLesson(l.id, { en });
  });

  const moveAgency = (a: SopAgency, dir: -1 | 1) => {
    const i = active.findIndex((x) => x.id === a.id);
    const other = active[i + dir];
    if (!other) return;
    const ao = a.order === other.order ? i + 1 : a.order;
    const oo = a.order === other.order ? i + 1 + dir : other.order;
    run(async () => {
      await updateAgency(a.id, (x) => ({ ...x, order: oo }), byName);
      await updateAgency(other.id, (x) => ({ ...x, order: ao }), byName);
    });
  };
  const archiveAgency = (a: SopAgency) => run(() => updateAgency(a.id, (x) => ({ ...x, archived: true }), byName), `${a.name} archived. Its pending points and PDFs are kept`,
    async () => { await updateAgency(a.id, (x) => ({ ...x, archived: false }), byName); }).then((ok) => { if (ok) { setSettingsOpen(false); setAgencyId(''); } });

  // ---------- Empty and loading ----------
  if (loading) return <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>;
  if (!agencies.length || !current) {
    return <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 1 }}>Agency SOPs</Typography>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 3, p: 3, bgcolor: '#fff' }}>
        <Typography sx={{ fontFamily: HEAD_FONT, fontSize: 22, mb: 1 }}>No SOPs yet</Typography>
        <Typography sx={{ color: MUTED, mb: 2, maxWidth: '62ch' }}>
          Start with ready SOPs for Civil, Plumber, Electrician, Stone & Flooring, Carpenter, Aluminium and Painter, plus common points for every agency.
          Plumber and Common include Gujarati. Edit everything afterwards.
        </Typography>
        {canManage
          ? <Button variant="contained" onClick={() => run(async () => { const n = await seedStarterSops(byName); if (!n) throw new Error('Starter SOPs are already loaded.'); }, 'Starter SOPs loaded')}>Load starter SOPs</Button>
          : <Alert severity="info">An admin needs to set up the SOPs first.</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Box>
    </Box>;
  }

  // ---------- Pieces ----------
  const a = current;
  const isC = a.id === COMMON_SOP_ID;
  // The draft is shown while there is one; "See approved" shows what PDFs use.
  const inDraft = !showApproved && !!a.draft;
  const readOnly = showApproved && !!a.draft;
  const va: SopAgency = inDraft ? { ...a, clauses: workingClauses(a) } : a;
  const liveRows = allRows(va, null);
  const commonRows = !isC && common ? allRows(common, null) : [];
  const readiness = guReadiness([...liveRows, ...commonRows]);
  const summary = draftSummary(a);
  const blockers = a.draft ? publishBlockers(a.draft.clauses) : [];
  const pdfBlockers = pdfRows({ agency: a, common: isC ? null : common }).filter((r) => guState(r.row) !== 'ok').length;
  const openProposals = (a.review?.proposals || []).filter((p) => p.status === 'open').length;
  const n = newCount(a.id);
  const nameGu = isC ? SOP_LABELS_GU.common : a.nameGu;
  const canEdit = canManage && !readOnly;
  const siteTeam = !!a.siteTeam;
  const previewValues = sampleValues(a, common);
  const report = placeholderReport([...liveRows, ...commonRows]);
  const issueCount = report.issues.length;

  /** Open the SOP tab at a point and highlight it. */
  const goToPoint = (row: SopRow) => {
    setTab('sop');
    if (row.num.startsWith('C') && !isC) setCommonOpen(true);
    setFlash([row.id]);
    setTimeout(() => document.getElementById(`sop-pt-${row.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
  };
  const saveSample = (key: string, next: SopSample) => run(() => updateAgency(a.id, (x) => {
    const samples = { ...(x.samples || {}) };
    const clean: SopSample = {};
    if (next.en?.trim()) clean.en = next.en.trim();
    if (next.gu?.trim()) clean.gu = next.gu.trim();
    if (clean.en || clean.gu) samples[key] = clean; else delete samples[key];
    return { ...x, samples };
  }, byName), 'Sample saved');

  const guTag = (row: SopRow) => {
    const s = guState(row);
    if (s === 'ai') return <Box component="span" sx={tagSx(BLUE_SOFT, BLUE)}><AutoAwesomeOutlinedIcon sx={{ fontSize: 13 }} />AI draft, check it</Box>;
    if (s === 'stale') return <Box component="span" sx={tagSx(WARN_SOFT, WARN)}><WarningAmberOutlinedIcon sx={{ fontSize: 13 }} />English changed, check Gujarati</Box>;
    if (s === 'missing') return <Box component="span" sx={tagSx(SUNK, MUTED)}>Not in Gujarati yet</Box>;
    return lang === 'both' ? <Box component="span" sx={tagSx(GREEN_SOFT, GREEN)}><CheckCircleOutlineIcon sx={{ fontSize: 13 }} />Checked</Box> : null;
  };

  const sectionTitle = (si: number) => (lang === 'gu'
    ? <Box component="span" lang="gu" sx={{ fontFamily: GU_FONT }}>{SOP_SECTIONS_GU[si]}</Box>
    : lang === 'both'
      ? <>{SOP_SECTIONS[si]} <Box component="span" lang="gu" sx={{ fontFamily: GU_FONT, fontWeight: 600 }}>{SOP_SECTIONS_GU[si]}</Box></>
      : SOP_SECTIONS[si]);

  const renderPoint = (owner: SopAgency, row: SopRow, inCommonBlock = false) => {
    const isEditing = editing && editing.agencyId === owner.id && editing.pointId === row.id ? editing.kind : null;
    const editable = canEdit && !inCommonBlock;
    const mark = inDraft && !inCommonBlock ? draftMark(row, a.clauses) : null;
    const markTag = mark ? <Box component="span" sx={tagSx(BLUE_SOFT, BLUE)}>{mark === 'new' ? 'New in draft' : 'Changed in draft'}</Box> : null;
    const bg = flash.includes(row.id) ? GOLD_SOFT : row.hold ? HOLD_SOFT : 'transparent';
    const holdTag = row.hold
      ? <Box component="span" lang={lang === 'gu' ? 'gu' : undefined} sx={{ ...tagSx('#fff', HOLD, 'rgba(179,67,43,0.35)'), fontFamily: lang === 'gu' ? GU_FONT : undefined }}><FrontHandOutlinedIcon sx={{ fontSize: 12 }} />{lang === 'gu' ? SOP_LABELS_GU.hold : 'Hold point'}</Box> : null;
    const textSx = { maxWidth: '72ch', color: DARK, fontSize: 13.5 };
    const gu = guText(row);
    const guSx = { ...textSx, fontFamily: GU_FONT, lineHeight: 1.65 };
    const pvEn = preview ? previewValues.en : undefined;
    const pvGu = preview ? previewValues.gu : undefined;
    const fallback = <Typography sx={{ ...textSx, color: FAINT, fontStyle: 'italic' }}>{lang === 'both' ? 'Not translated' : <PlaceholderText text={row.text} values={pvEn} />}</Typography>;

    let body: React.ReactNode;
    if (lang === 'en') {
      body = <><Typography sx={textSx}><PlaceholderText text={row.text} values={pvEn} /></Typography><TagRow>{holdTag}{markTag}</TagRow></>;
    } else if (lang === 'gu') {
      body = <>{gu ? <Typography lang="gu" sx={guSx}><PlaceholderText text={gu} gu values={pvGu} /></Typography> : fallback}<TagRow>{holdTag}{markTag}{guTag(row)}</TagRow></>;
    } else {
      // English on top, Gujarati directly below it.
      body = <Stack gap={0.5}>
        <Typography sx={textSx}><PlaceholderText text={row.text} values={pvEn} /></Typography>
        {gu ? <Typography lang="gu" sx={{ ...guSx, color: '#3d3a35' }}><PlaceholderText text={gu} gu values={pvGu} /></Typography> : fallback}
        <TagRow>{holdTag}{markTag}{guTag(row)}</TagRow>
      </Stack>;
    }

    const actions: React.ReactNode[] = [];
    if (editable) {
      if (lang !== 'en') actions.push(<IconAction key="gu" title="Edit Gujarati" onClick={() => { closeEdit(); setEditing({ kind: 'gu', agencyId: owner.id, pointId: row.id }); setEditText(gu); }}><TranslateIcon fontSize="small" /></IconAction>);
      actions.push(<IconAction key="below" title="Add a point below (to the draft)" onClick={() => setDrawer({ kind: 'point', pre: { targets: [owner.id], section: row.section, after: row.id } })}><PlaylistAddOutlinedIcon fontSize="small" /></IconAction>);
      actions.push(<IconAction key="edit" title="Edit English" onClick={() => { closeEdit(); setEditing({ kind: 'en', agencyId: owner.id, pointId: row.id }); setEditText(row.text); setEditHold(row.hold); }}><EditOutlinedIcon fontSize="small" /></IconAction>);
      actions.push(<IconAction key="del" title="Remove" danger onClick={() => removePoint(owner, row.id)}><DeleteOutlineIcon fontSize="small" /></IconAction>);
    }

    const num = <Typography sx={{ fontSize: 12, fontWeight: 700, color: row.hold ? HOLD : FAINT, pt: '1px', pl: 1.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{row.num}</Typography>;
    const shell = (children: React.ReactNode) => <Box key={row.id} id={`sop-pt-${row.id}`} sx={{ scrollMarginTop: 80, display: 'grid', gridTemplateColumns: { xs: '34px minmax(0,1fr)', sm: '44px minmax(0,1fr) auto' }, gap: { xs: 1, sm: 1.5 }, py: 1.4, pr: 1, borderBottom: `1px solid ${BORDER}`, bgcolor: bg, transition: 'background-color 600ms ease', alignItems: 'start', '&:hover .sop-acts, &:focus-within .sop-acts': { opacity: 1 } }}>{children}</Box>;

    if (isEditing === 'en') {
      return shell(<>{num}<Stack gap={1.25} sx={{ gridColumn: { xs: '2', sm: '2 / -1' }, bgcolor: SUNK, borderRadius: 2, p: 1.75 }}>
        <SopTextField label="Point in English" value={editText} onChange={setEditText} autoFocus />
        <FormControlLabel control={<Checkbox checked={editHold} onChange={(e) => setEditHold(e.target.checked)} sx={{ '&.Mui-checked': { color: HOLD } }} />} label={<Hint title="Hold point" sub="Work stops here until the engineer signs." />} />
        {gu && <Typography sx={{ fontSize: 12, color: MUTED }}>If you change the meaning, the Gujarati is marked "check Gujarati".</Typography>}
        <Stack direction="row" gap={1}><Button variant="contained" size="small" onClick={() => saveEnglish(owner, row.id)}>Save to draft</Button><Button size="small" sx={{ color: MUTED }} onClick={closeEdit}>Cancel</Button></Stack>
      </Stack></>);
    }
    if (isEditing === 'gu') {
      const key = 'gu-' + row.id;
      return shell(<>{num}<Stack gap={1.25} sx={{ gridColumn: { xs: '2', sm: '2 / -1' }, bgcolor: SUNK, borderRadius: 2, p: 1.75 }}>
        <Box sx={{ bgcolor: '#fff', borderRadius: 1.5, px: 1.25, py: 1 }}><Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>English (master)</Typography><Typography sx={{ fontSize: 12.5, color: MUTED }}><PlaceholderText text={row.text} /></Typography></Box>
        <SopTextField label="ગુજરાતી" gu value={editText} onChange={setEditText} autoFocus placeholder="Type the Gujarati, or use AI translate and then correct it"
          extra={<AiButton busy={!!busy[key]} label="AI translate" onClick={() => withBusy(key, async () => { const [out] = await translateTexts([row.text], 'gu'); setEditText(out); })} />} />
        <Stack direction="row" gap={1}><Button variant="contained" size="small" startIcon={<CheckCircleOutlineIcon />} onClick={() => saveGujarati(owner, row)}>Save as checked</Button><Button size="small" sx={{ color: MUTED }} onClick={closeEdit}>Cancel</Button></Stack>
      </Stack></>);
    }
    return shell(<>{num}
      <Stack gap={0.6} sx={{ minWidth: 0 }}>{body}</Stack>
      <Stack className="sop-acts" direction="row" gap={0.25} alignItems="center" sx={{ gridColumn: { xs: '2', sm: 'auto' }, opacity: { md: 0.45 }, transition: 'opacity 150ms ease' }}>{actions}</Stack>
    </>);
  };

  const commonBlock = common && !isC ? (() => {
    const holds = commonRows.filter((r) => r.hold).length;
    return <Box sx={{ border: '1px dashed rgba(157,111,50,0.45)', borderRadius: 2.5, mt: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ px: 1.75, py: 1.25, cursor: 'pointer' }} onClick={() => setCommonOpen((v) => !v)}>
        <ExpandMoreIcon sx={{ color: MUTED, transform: commonOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 200ms cubic-bezier(0.23,1,0.32,1)' }} fontSize="small" />
        <Typography lang={lang === 'gu' ? 'gu' : undefined} sx={{ fontWeight: 700, fontFamily: lang === 'gu' ? GU_FONT : undefined }}>{lang === 'gu' ? SOP_LABELS_GU.common : 'Common for all agencies'}</Typography>
        <Typography sx={{ color: MUTED, fontSize: 12.5 }}>{commonRows.length} points, {holds} hold point{holds === 1 ? '' : 's'}, v{common.version}</Typography>
        <Button size="small" sx={{ ml: 'auto' }} onClick={(e) => { e.stopPropagation(); setAgencyId(COMMON_SOP_ID); setSettingsOpen(false); }}>Open Common SOP</Button>
      </Stack>
      <Collapse in={commonOpen} unmountOnExit>
        <Stack gap={1.75} sx={{ px: 1.75, pb: 1.25 }}>
          {SOP_SECTIONS.map((_, si) => {
            const r = sectionRows(common, null, si);
            return r.length ? <Box key={si}><Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 600, mb: 0.5 }}>{sectionTitle(si)}</Typography><Box sx={{ borderTop: `1px solid ${BORDER}` }}>{r.map((row) => renderPoint(common, row, true))}</Box></Box> : null;
          })}
        </Stack>
      </Collapse>
    </Box>;
  })() : null;

  const langBar = lang !== 'en' ? (() => {
    const missing = liveRows.filter((r) => guState(r) === 'missing').length;
    return <Box sx={{ mt: 2, border: `1px solid ${BORDER}`, borderRadius: 2.5, px: 1.75, py: 1.5 }}>
      <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" justifyContent="space-between">
        {readiness.ready
          ? <Typography sx={{ fontWeight: 700, color: GREEN, display: 'flex', gap: 0.75, alignItems: 'center' }}><CheckCircleOutlineIcon fontSize="small" />Ready to give in Gujarati</Typography>
          : <Typography sx={{ fontWeight: 700, color: WARN, display: 'flex', gap: 0.75, alignItems: 'center' }}><WarningAmberOutlinedIcon fontSize="small" />Not ready in Gujarati: {readiness.total - readiness.ok} of {readiness.total} points need work</Typography>}
        {canEdit && missing > 0 && <AiButton outlined busy={!!busy['batch-' + a.id]} label={`Translate ${missing} missing with AI`} onClick={() => translateMissing(a)} />}
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1.75} sx={{ mt: 1, fontSize: 12.5, color: MUTED }}>
        <span><b style={{ color: DARK }}>{readiness.ok}</b> checked</span>
        <span><b style={{ color: DARK }}>{readiness.ai}</b> AI drafts to check</span>
        <span><b style={{ color: DARK }}>{readiness.stale}</b> need check after English change</span>
        <span><b style={{ color: DARK }}>{readiness.missing}</b> not translated</span>
        {!isC && <span>Includes common points (translate those on the Common page).</span>}
      </Stack>
      <Box component="details" sx={{ mt: 1, fontSize: 12.5, color: MUTED, '& summary': { cursor: 'pointer', fontWeight: 600, width: 'fit-content' } }}>
        <summary>Site Gujarati word list ({SOP_GLOSSARY.length})</summary>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', columnGap: 2, rowGap: 0.5, mt: 1 }}>
          {SOP_GLOSSARY.map(([en, g]) => <Stack key={en} direction="row" justifyContent="space-between" sx={{ borderBottom: `1px solid ${BORDER}`, py: 0.4 }}><span>{en}</span><span lang="gu" style={{ color: DARK, fontFamily: GU_FONT }}>{g}</span></Stack>)}
        </Box>
        <Typography sx={{ fontSize: 12, color: MUTED, mt: 1 }}>Always kept in English letters: {SOP_KEEP_AS_IS}, and placeholders like {'{{retention_percent}}'}.</Typography>
      </Box>
    </Box>;
  })() : null;

  /** Approved vs draft, and the publish step. */
  const draftBar = canManage ? (a.draft ? <Box sx={{ mt: 2, border: `1px solid ${showApproved ? BORDER : 'rgba(47,95,150,0.35)'}`, bgcolor: showApproved ? '#fff' : BLUE_SOFT, borderRadius: 2.5, px: 1.75, py: 1.5 }}>
    <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" justifyContent="space-between">
      <Box>
        <Typography sx={{ fontWeight: 700, color: showApproved ? DARK : BLUE }}>{showApproved ? `Showing approved v${a.version}` : `Draft v${a.version + 1}`}</Typography>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>{summary.added} added, {summary.changed} changed, {summary.removed} removed since v{a.version}. PDFs keep using v{a.version} until you publish.</Typography>
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button size="small" sx={{ color: MUTED }} onClick={() => { setShowApproved((v) => !v); closeEdit(); }}>{showApproved ? 'Back to draft' : `See approved v${a.version}`}</Button>
        <Button size="small" color="error" onClick={() => discard(a)}>Discard draft</Button>
        <Tooltip title={blockers.length ? `${blockers.length} point${blockers.length === 1 ? ' needs' : 's need'} checked Gujarati first` : ''}>
          <span><Button size="small" variant="contained" startIcon={<PublishOutlinedIcon />} disabled={!!blockers.length || summary.total === 0} onClick={() => publish(a)}>Publish v{a.version + 1}</Button></span>
        </Tooltip>
      </Stack>
    </Stack>
    {blockers.length > 0 && !showApproved && <Typography sx={{ fontSize: 12.5, color: WARN, mt: 1 }}>
      Every PDF is bilingual, so {blockers.length} point{blockers.length === 1 ? ' needs' : 's need'} checked Gujarati before publishing. {lang === 'en' && <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: 12.5 }} onClick={() => setLang('both')}>Show them</Button>}
    </Typography>}
  </Box> : <Typography sx={{ mt: 2, fontSize: 12.5, color: MUTED }}>Approved v{a.version}. Any change you make here starts draft v{a.version + 1}; PDFs keep using v{a.version} until you publish.</Typography>)
    : <Banner grey>This is approved v{a.version}. New points go to <b>Pending points</b> first and are reviewed before they change the SOP.</Banner>;

  const pdfButton = <Tooltip title={pdfBlockers ? `${pdfBlockers} point${pdfBlockers === 1 ? '' : 's'} in approved v${a.version} need checked Gujarati first` : `Fill in the project details and make the bilingual PDF of approved v${a.version}`}>
    <span><Button size="small" variant="contained" startIcon={<PictureAsPdfOutlinedIcon />} disabled={!!pdfBlockers} onClick={() => setPdfOpen(true)}>Generate SOP PDF</Button></span>
  </Tooltip>;

  const sopBody = <>
    {draftBar}
    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
      <FormControlLabel sx={{ mr: 0 }} control={<Switch size="small" checked={preview} onChange={(e) => { setPreview(e.target.checked); closeEdit(); }} />}
        label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Preview with samples</Typography>} />
      {preview && <Typography sx={{ fontSize: 12, color: MUTED }}>Points show as the PDF prints them, with <Box component="span" sx={{ color: BLUE, fontWeight: 600, borderBottom: '1.5px dotted rgba(47,95,150,0.6)' }}>sample values</Box>. Change samples on the Placeholders tab.</Typography>}
    </Stack>
    {langBar}
    {commonBlock}
    {SOP_SECTIONS.map((_, si) => {
      const rows = sectionRows(va, null, si);
      return <Box key={si} sx={{ mt: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>{sectionTitle(si)}<Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: FAINT }}>{rows.length} point{rows.length === 1 ? '' : 's'}</Box></Typography>
          {canEdit && <Button size="small" startIcon={<AddIcon />} onClick={() => setDrawer({ kind: 'point', pre: { targets: [a.id], section: si } })}>Add to draft</Button>}
        </Stack>
        <Box sx={{ borderTop: `1px solid ${BORDER}` }}>{rows.map((row) => renderPoint(a, row))}</Box>
        {!rows.length && <Typography sx={{ fontSize: 12, color: MUTED, py: 1.25 }}>No points in this section yet.</Typography>}
      </Box>;
    })}
    <Box component="details" sx={{ mt: 3, fontSize: 12.5, color: MUTED, '& summary': { cursor: 'pointer', fontWeight: 700, color: DARK, width: 'fit-content' } }}>
      <summary>Change history ({a.log.length})</summary>
      <Stack gap={0.75} sx={{ mt: 1 }}>{[...a.log].reverse().map((l, i) => <Stack key={i} direction="row" gap={1.25}><b style={{ color: GOLD_D, minWidth: 28 }}>{l.gu ? 'GU' : `v${l.v}`}</b><span>{l.note}{l.by ? ` (${l.by}${l.at ? `, ${new Date(l.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''})` : ''}</span></Stack>)}</Stack>
      {a.draft && a.draft.notes.length > 0 && <Box sx={{ mt: 1.5 }}><Typography sx={{ fontSize: 12, fontWeight: 700, color: BLUE }}>In draft v{a.version + 1}</Typography>
        <Stack gap={0.5} sx={{ mt: 0.5 }}>{[...a.draft.notes].reverse().map((note, i) => <span key={i}>{note}</span>)}</Stack></Box>}
    </Box>
    <Box component="details" sx={{ mt: 1.5, fontSize: 12.5, color: MUTED, '& summary': { cursor: 'pointer', fontWeight: 700, color: DARK, width: 'fit-content' } }}>
      <summary>PDFs made ({pdfs.length})</summary>
      <Stack gap={0.75} sx={{ mt: 1 }}>
        {pdfs.length ? pdfs.map((p) => <Stack key={p.id} direction="row" gap={1.25} flexWrap="wrap" alignItems="center">
          <b style={{ color: GOLD_D, minWidth: 28 }}>v{p.version}</b>
          <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>{p.projectName}{p.firm ? `, ${p.firm}` : ''}</a>
          <span>{p.createdByName}, {new Date(p.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
        </Stack>) : <span>No PDF made yet. Use "Generate SOP PDF" at the top.</span>}
      </Stack>
    </Box>
  </>;

  const agencyLessons = lessons.filter((l) => l.agencyId === a.id);
  const shownLessons = agencyLessons.filter((l) => (lessonFilter === 'new' ? l.status === 'new' : lessonFilter === 'done' ? l.status !== 'new' : true));
  const lessonsBody = <>
    {canManage && n > 0 && <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center" justifyContent="space-between" sx={{ mt: 2, border: '1px dashed rgba(47,95,150,0.45)', borderRadius: 2.5, px: 1.75, py: 1.5 }}>
      <Box sx={{ maxWidth: '62ch' }}>
        <Typography sx={{ fontWeight: 700 }}>Draft new version with AI</Typography>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>AI reads all {n} pending point{n === 1 ? '' : 's'}, joins repeats, skips what the SOP or Common points already cover, and writes proper points in English and site Gujarati, with placeholders for values that change by project. You review each one before it goes into the draft.</Typography>
      </Box>
      <AiButton outlined busy={!!busy['draft-' + a.id]} label={`Draft from ${n} pending`} onClick={() => draftAi(a)} />
    </Stack>}
    <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
      <ToggleButtonGroup size="small" exclusive value={lessonFilter} onChange={(_, v) => v && setLessonFilter(v)} sx={segSx}>
        <ToggleButton value="new">Pending ({n})</ToggleButton><ToggleButton value="done">Done</ToggleButton><ToggleButton value="all">All</ToggleButton>
      </ToggleButtonGroup>
      <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDrawer({ kind: 'lesson' })}>Add pending point</Button>
    </Stack>
    {shownLessons.length ? <Box sx={{ borderTop: `1px solid ${BORDER}`, mt: 2 }}>{shownLessons.map((l) => {
      const notEnglish = hasGujaratiScript(l.what + (l.suggest || '')) || /\b(ma|che|nathi|hatu|hato|pase|bija|karvanu|nai|thay|chhe)\b/i.test(`${l.what} ${l.suggest || ''}`);
      const imp = SOP_IMPACTS.find((x) => x.value === l.impact);
      const impSx = l.impact === 'rework' || l.impact === 'safety' ? tagSx(HOLD_SOFT, HOLD) : l.impact === 'quality' ? tagSx(BLUE_SOFT, BLUE) : l.impact === 'good' ? tagSx(GREEN_SOFT, GREEN) : tagSx(GOLD_SOFT, GOLD_D);
      const main = l.suggest || l.what;
      const context = l.suggest ? l.what : '';
      return <Stack key={l.id} gap={1} sx={{ py: 2, borderBottom: `1px solid ${BORDER}` }}>
        <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" sx={{ fontSize: 12, color: MUTED }}>
          <Box component="span" sx={impSx}>{imp?.label || l.impact}</Box>
          {l.createdByName && <Box component="span" sx={{ color: DARK, fontWeight: 600 }}>{l.createdByName}</Box>}<span>{l.date}</span>
        </Stack>
        <Typography lang={hasGujaratiScript(main) ? 'gu' : undefined} sx={{ maxWidth: '72ch', fontWeight: 500, fontFamily: hasGujaratiScript(main) ? GU_FONT : undefined }}>{main}</Typography>
        {context && <Box sx={{ bgcolor: SUNK, borderRadius: 2, px: 1.5, py: 1, maxWidth: '76ch' }}><Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>What happened</Typography><Typography lang={hasGujaratiScript(context) ? 'gu' : undefined} sx={{ fontSize: 13, fontFamily: hasGujaratiScript(context) ? GU_FONT : undefined }}>{context}</Typography></Box>}
        {l.en && <Box sx={{ bgcolor: BLUE_SOFT, borderRadius: 2, px: 1.5, py: 1, maxWidth: '76ch' }}><Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>In English (AI)</Typography><Typography sx={{ fontSize: 13 }}>{l.en}</Typography></Box>}
        {notEnglish && !l.en && <Box><AiButton busy={!!busy['en-' + l.id]} label="Show in English" onClick={() => lessonEnglish(l)} /></Box>}
        {l.status === 'new' ? (canManage && <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button variant="outlined" size="small" onClick={() => { setShowApproved(false); setDrawer({ kind: 'point', pre: { targets: [l.agencyId], section: 1, en: hasGujaratiScript(l.suggest) ? '' : l.suggest, gu: hasGujaratiScript(l.suggest) ? l.suggest : '', lessonId: l.id } }); }}>Add to draft myself</Button>
          <Button size="small" onClick={() => markLesson(l, 'duplicate')}>Already covered</Button>
          <Button size="small" onClick={() => markLesson(l, 'vendor')}>Vendor note only</Button>
          <Button size="small" sx={{ color: MUTED }} onClick={() => markLesson(l, 'rejected')}>Not needed</Button>
        </Stack>) : <Stack direction="row" gap={1.25} alignItems="center" sx={{ fontSize: 12 }}>
          <Box component="span" sx={{ fontWeight: 600, color: l.status === 'added' ? GREEN : l.status === 'vendor' ? BLUE : MUTED, display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>{l.status === 'added' && <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}{l.status === 'added' ? 'Used' : l.status === 'vendor' ? 'Kept as vendor note' : l.status === 'duplicate' ? 'Already covered' : 'Marked not needed'}</Box>
          {l.result && <Box component="span" sx={{ color: MUTED }}>{l.result}</Box>}
        </Stack>}
      </Stack>;
    })}</Box> : <Stack gap={1} alignItems="flex-start" sx={{ py: 3.5, color: MUTED }}>
      <Typography sx={{ fontWeight: 700, color: DARK }}>{lessonFilter === 'new' ? `No pending points for ${isC ? 'common points' : a.name}` : 'Nothing here yet'}</Typography>
      {lessonFilter === 'new' && <Typography sx={{ fontSize: 13, maxWidth: '62ch' }}>Write any idea or site experience here in any language, without formatting. Pending points are turned into proper SOP points when the next version is drafted.</Typography>}
    </Stack>}
  </>;

  const proposals = a.review?.proposals || [];
  const reviewBody = proposals.length ? <>
    <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: 12.5, color: MUTED, maxWidth: '64ch' }}>
        Proposals {proposals.some((p) => p.source) ? 'from the old SOP and pending points' : 'from pending points'}, made on {a.review?.at ? new Date(a.review.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : ''}{a.review?.by ? ` by ${a.review.by}` : ''}. Accept puts a point into draft v{a.version + 1}. Reject leaves the SOP unchanged{proposals.some((p) => !p.source) ? ' and keeps pending points pending' : ''}.
      </Typography>
      {openProposals === 0 && <Button size="small" sx={{ color: MUTED }} onClick={() => run(() => saveReview(a.id, null, byName), 'Review cleared')}>Clear finished review</Button>}
    </Stack>
    {openProposals === 0 && <Banner>Review done. Open the <b>SOP</b> tab to check draft v{a.version + 1} and publish it.</Banner>}
    <Stack gap={1.5} sx={{ mt: 2 }}>
      {proposals.map((p) => <ProposalCard key={p.id} proposal={p} sources={lessons.filter((l) => p.sourceIds.includes(l.id))}
        target={p.targetId ? [...allRows({ ...a, clauses: workingClauses(a) }, null), ...(common && !isC ? allRows(common, null) : [])].find((r) => r.id === p.targetId) : undefined}
        canDecide={canManage} onDecide={(edited, accept, guChecked) => decideProposal(a, edited, accept, guChecked)} />)}
    </Stack>
  </> : <Stack gap={1} alignItems="flex-start" sx={{ py: 3.5, color: MUTED }}>
    <Typography sx={{ fontWeight: 700, color: DARK }}>Nothing to review</Typography>
    <Typography sx={{ fontSize: 13, maxWidth: '62ch' }}>On the Pending points tab, use "Draft new version with AI". The AI's proposals show here for you to accept, edit or reject one by one.</Typography>
  </Stack>;

  const ISSUE_TEXT: Record<PlaceholderIssueKind, (labels: string) => string> = {
    'gu-mismatch': (l) => `English and Gujarati use different placeholders (${l}). The two languages would print different values.`,
    'header-in-point': (l) => `${l} belongs in the PDF header only, not inside a point.`,
    'long-in-sentence': (l) => `${l} is a long text. Inside a sentence it reads badly; it already prints in the Key terms table.`,
    unknown: (l) => `${l} is not a known placeholder (probably a typo). It would print as written.`,
  };
  const labelsOf = (keys: string[]) => keys.map((k) => placeholderByKey(k)?.label || `{{${k}}}`).join(', ');
  const termRows = SOP_PLACEHOLDERS.filter((p) => !p.header);
  const headerRows = SOP_PLACEHOLDERS.filter((p) => p.header);
  const unused = termRows.filter((p) => !report.usage[p.key]?.length);
  const placeholderTable = (list: typeof SOP_PLACEHOLDERS, header: boolean) => <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: 'hidden' }}>
    <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '1.3fr 1fr 1.6fr 1.6fr', gap: 1.5, px: 1.5, py: 1, bgcolor: SUNK, fontSize: 11.5, fontWeight: 700, color: MUTED }}>
      <span>Placeholder</span><span>{header ? 'Where it prints' : 'Used in'}</span><span>Sample (English)</span><span lang="gu" style={{ fontFamily: GU_FONT }}>Sample (ગુજરાતી)</span>
    </Box>
    {list.map((p) => {
      const used = report.usage[p.key] || [];
      return <Box key={p.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr 1.6fr 1.6fr' }, gap: 1.5, px: 1.5, py: 1.25, borderTop: `1px solid ${BORDER}`, alignItems: 'start' }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{p.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: FAINT, fontFamily: 'monospace' }}>{`{{${p.key}}}`}</Typography>
        </Box>
        <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
          {header ? <Typography sx={{ fontSize: 12, color: MUTED }}>PDF details table</Typography>
            : used.length ? used.map((r) => <Chip key={r.id} size="small" label={r.num} onClick={() => goToPoint(r)} sx={{ height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: GOLD_SOFT, color: GOLD_D }} />)
              : <Typography sx={{ fontSize: 12, color: FAINT }}>Not used</Typography>}
        </Stack>
        <SampleEditor key={`${a.id}-${p.key}`} placeholderKey={p.key} agency={a} common={common} canEdit={canManage} onSave={(next) => saveSample(p.key, next)} />
      </Box>;
    })}
  </Box>;
  const placeholdersBody = <>
    <Typography sx={{ mt: 2, fontSize: 12.5, color: MUTED, maxWidth: '76ch' }}>
      {isC
        ? 'Samples set here are the defaults for every agency. Each agency can override them on its own Placeholders tab.'
        : `Placeholders in the ${a.name} SOP and the Common points, with sample values. Samples show in "Preview with samples" and as grey examples in the PDF pop-up; they are never printed on their own.`}
    </Typography>
    <Box sx={{ mt: 2, border: `1px solid ${issueCount ? 'rgba(168,100,12,0.35)' : 'rgba(60,107,75,0.3)'}`, bgcolor: issueCount ? WARN_SOFT : GREEN_SOFT, borderRadius: 2.5, px: 1.75, py: 1.5 }}>
      <Typography sx={{ fontWeight: 700, color: issueCount ? WARN : GREEN, display: 'flex', gap: 0.75, alignItems: 'center' }}>
        {issueCount ? <WarningAmberOutlinedIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
        {issueCount ? `${issueCount} point${issueCount === 1 ? '' : 's'} to fix` : 'All placeholders make sense'}
      </Typography>
      {report.issues.map((iss, i) => <Stack key={i} direction="row" gap={1} alignItems="baseline" sx={{ mt: 0.75, fontSize: 12.5 }}>
        <Button size="small" onClick={() => goToPoint(iss.row)} sx={{ minWidth: 0, p: 0, fontWeight: 800, color: WARN }}>{iss.row.num}</Button>
        <span>{ISSUE_TEXT[iss.kind](labelsOf(iss.keys))}</span>
      </Stack>)}
      {unused.length > 0 && <Typography sx={{ mt: 1, fontSize: 12.5, color: MUTED }}>
        Not used anywhere, so the PDF says nothing about: {unused.map((p) => p.label).join(', ')}. Fine if that does not apply to {isC ? 'every agency' : a.name}.
      </Typography>}
    </Box>
    <Typography sx={{ mt: 2.5, mb: 1, fontSize: 13, fontWeight: 800 }}>Terms (used inside points)</Typography>
    {placeholderTable(termRows, false)}
    <Typography sx={{ mt: 2.5, mb: 1, fontSize: 13, fontWeight: 800 }}>PDF header only</Typography>
    {placeholderTable(headerRows, true)}
    <Typography sx={{ mt: 1.5, fontSize: 12, color: FAINT }}>Need a new placeholder? Ask for it to be added; the list is shared with the AI rules.</Typography>
  </>;

  const checkItems = a.checklist || [];
  const checkAnswers = checklists[a.id] || {};
  const checkStat = checklistStatus(checkItems, checkAnswers);
  const checkProjectName = projects.find((p) => p.id === checkProjectId)?.name || '';
  const saveAnswer = (it: SopCheckItem, patch: SopCheckAnswer) => run(() => saveChecklistAnswer(checkProjectId, a.id, it.id, patch, byName));
  const checklistBody = editingChecklist && canManage
    ? <ChecklistEditor key={a.id} items={checkItems} onCancel={() => setEditingChecklist(false)}
      onSave={(items) => run(() => updateAgency(a.id, (x) => ({ ...x, checklist: items }), byName), 'Checklist saved').then((ok) => { if (ok) setEditingChecklist(false); })} />
    : <>
      <Typography sx={{ mt: 2, fontSize: 12.5, color: MUTED, maxWidth: '76ch' }}>
        {siteTeam
          ? 'What the site team must decide or do before construction starts on a project. This list is not given to any agency.'
          : <>What must be decided or done before {isC ? 'any agency' : a.name} starts on a project. <b>Decisions</b> (brand, grade, model) print in the SOP PDF as "Approved materials". <b>Tasks</b> are for the site team and never print.</>}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1.25} alignItems="center" sx={{ mt: 2 }}>
        <TextField select size="small" label="Project" value={checkProjectId} onChange={(e) => setCheckProjectId(String(e.target.value))} sx={{ minWidth: 220 }}>
          {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        {checkItems.length > 0 && <Box component="span" sx={tagSx(checkStat.ready === checkStat.total ? GREEN_SOFT : WARN_SOFT, checkStat.ready === checkStat.total ? GREEN : WARN)}>
          {checkStat.ready === checkStat.total ? 'Ready to start' : `${checkStat.ready} of ${checkStat.total} ready`}
        </Box>}
        <Box sx={{ flex: 1 }} />
        {canManage && <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setEditingChecklist(true)}>Edit items</Button>}
      </Stack>
      {!checkItems.length ? <Stack gap={1} alignItems="flex-start" sx={{ py: 3.5, color: MUTED }}>
        <Typography sx={{ fontWeight: 700, color: DARK }}>No checklist items yet</Typography>
        <Typography sx={{ fontSize: 13, maxWidth: '62ch' }}>Add what must be decided (e.g. pipe brand, grade) and done (e.g. brand training) before this agency starts.</Typography>
        {canManage && <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setEditingChecklist(true)}>Add items</Button>}
      </Stack> : !checkProjectId ? <Typography sx={{ mt: 2, fontSize: 13, color: MUTED }}>Pick a project to fill in the checklist.</Typography> : <>
        {(['decision', 'task'] as const).map((kind) => {
          const list = checkItems.filter((it) => it.kind === kind);
          if (!list.length) return null;
          return <Box key={kind} sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1 }}>{kind === 'decision' ? 'Decisions' : 'Tasks'} <Box component="span" sx={{ fontWeight: 500, color: FAINT, fontSize: 12 }}>{kind === 'decision' ? 'print in the PDF' : 'site team only'}</Box></Typography>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5 }}>
              {list.map((it, i) => {
                const ans = checkAnswers[it.id] || {};
                const meta = ans.by ? `${ans.by}${ans.at ? `, ${new Date(ans.at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}` : '';
                return <Box key={it.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: kind === 'decision' ? '1.1fr 1.4fr' : 'auto 1fr' }, gap: 1.5, alignItems: 'center', px: 1.5, py: 1.25, borderTop: i ? `1px solid ${BORDER}` : 0 }}>
                  {kind === 'task' && <Checkbox checked={!!ans.done} onChange={(e) => saveAnswer(it, { done: e.target.checked })} sx={{ p: 0.5, '&.Mui-checked': { color: GREEN } }} />}
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, textDecoration: kind === 'task' && ans.done ? 'line-through' : 'none', color: kind === 'task' && ans.done ? MUTED : DARK }}>{it.en}</Typography>
                    {it.gu && <Typography lang="gu" sx={{ fontSize: 12.5, color: MUTED, fontFamily: GU_FONT }}>{it.gu}</Typography>}
                    {kind === 'task' && meta && <Typography sx={{ fontSize: 11.5, color: FAINT }}>{ans.done ? 'Done' : 'Unticked'} by {meta}</Typography>}
                  </Box>
                  {kind === 'decision' && <Box>
                    <DecisionField key={`${checkProjectId}-${it.id}`} value={ans.value || ''} hint={it.hint} onSave={(value) => saveAnswer(it, { value })} />
                    {meta && <Typography sx={{ fontSize: 11.5, color: FAINT, mt: 0.4 }}>Set by {meta}</Typography>}
                  </Box>}
                </Box>;
              })}
            </Box>
          </Box>;
        })}
      </>}
    </>;

  const settingsRow = settingsOpen && !isC && canManage ? <AgencySettings key={a.id} agency={a} index={active.findIndex((x) => x.id === a.id)} count={active.length}
    onSaveNames={(name, nameGu) => run(() => updateAgency(a.id, (x) => (name !== x.name ? bumped({ ...x, name, nameGu }, `Renamed from ${x.name} to ${name}`) : { ...x, nameGu }), byName), 'Names saved')}
    onMove={(dir) => moveAgency(a, dir)} onArchive={() => archiveAgency(a)}
    onGroup={(group) => run(() => updateAgency(a.id, (x) => { const next = { ...x }; if (group) next.group = group; else delete next.group; return next; }, byName), 'Group saved')} /> : null;

  const railItem = (x: SopAgency) => {
    const cnt = newCount(x.id);
    const selected = a.id === x.id;
    const xc = x.id === COMMON_SOP_ID;
    const cs = x.checklist?.length && checkProjectId ? checklistStatus(x.checklist, checklists[x.id]) : null;
    const start = cs ? `, start ${cs.ready}/${cs.total}` : '';
    const sub = xc ? `Every agency follows these. ${x.clauses.length} points${start}`
      : x.siteTeam ? `Site team list${start}` : `v${x.version}, ${x.clauses.length} points${x.draft ? ', draft open' : ''}${start}`;
    return <Box key={x.id} component="button" type="button" onClick={() => { setAgencyId(x.id); setSettingsOpen(false); closeEdit(); }}
      sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', columnGap: 1, alignItems: 'center', textAlign: 'left', border: xc ? `1px dashed ${BORDER}` : wide ? 0 : `1px solid ${BORDER}`, bgcolor: selected ? GOLD_SOFT : 'transparent', borderRadius: 2, px: 1.5, py: wide ? 1.1 : 0.9, cursor: 'pointer', font: 'inherit', flex: 'none', mb: xc && wide ? 0.5 : 0, '&:hover': { bgcolor: selected ? GOLD_SOFT : SUNK } }}>
      <Typography sx={{ fontWeight: selected ? 700 : 600, color: selected ? GOLD_D : DARK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', gap: 0.75, alignItems: 'center', fontSize: 13.5 }}>{xc && <LayersOutlinedIcon sx={{ fontSize: 15 }} />}{xc ? 'Common' : x.name}</Typography>
      {cnt > 0 && <Box sx={{ gridRow: wide ? '1 / span 2' : 1, gridColumn: 2, minWidth: 20, height: 20, px: 0.75, borderRadius: 10, bgcolor: HOLD, color: '#fff', fontSize: 11, fontWeight: 800, display: 'grid', placeItems: 'center' }}>{cnt}</Box>}
      {wide && <Typography sx={{ gridColumn: 1, fontSize: 11.5, color: FAINT }}>{sub}</Typography>}
    </Box>;
  };

  const toggleGroup = (key: string) => setClosedGroups((prev) => {
    const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
    try { localStorage.setItem('sopClosedGroups', JSON.stringify(next)); } catch { /* storage blocked: keep in memory */ }
    return next;
  });
  const rail = <Stack direction={wide ? 'column' : 'row'} gap={wide ? 0.15 : 0.75} sx={wide ? { position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' } : { overflowX: 'auto', pb: 0.5 }}>
    {common && railItem(common)}
    {wide ? groupAgencies(active).map((g) => {
      // Keep the selected SOP visible even when its group is collapsed.
      const closed = closedGroups.includes(g.key) && !g.items.some((x) => x.id === a.id);
      const pending = g.items.reduce((sum, x) => sum + newCount(x.id), 0);
      return <Box key={g.key}>
        <Box component="button" type="button" onClick={() => toggleGroup(g.key)} aria-expanded={!closed}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', px: 1, pt: 1.25, pb: 0.5, textAlign: 'left' }}>
          <ExpandMoreIcon sx={{ fontSize: 16, color: FAINT, transform: closed ? 'rotate(-90deg)' : 'none', transition: 'transform 200ms cubic-bezier(0.23,1,0.32,1)' }} />
          <Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', flex: 1 }}>{g.label}</Typography>
          {closed && pending > 0 && <Box component="span" sx={{ minWidth: 18, height: 18, px: 0.6, borderRadius: 9, bgcolor: HOLD, color: '#fff', fontSize: 10.5, fontWeight: 800, display: 'grid', placeItems: 'center' }}>{pending}</Box>}
          <Typography sx={{ fontSize: 11, color: FAINT }}>{g.items.length}</Typography>
        </Box>
        <Collapse in={!closed}>{g.items.map(railItem)}</Collapse>
      </Box>;
    }) : active.map(railItem)}
    <Stack direction={wide ? 'column' : 'row'} gap={0.5} sx={{ pt: wide ? 1 : 0, flex: 'none' }}>
      {canManage && <Button size="small" startIcon={<AddIcon />} onClick={() => setDrawer({ kind: 'agency' })} sx={{ justifyContent: 'flex-start' }}>Add agency</Button>}
      {wide && archived.length > 0 && <Button size="small" sx={{ color: MUTED, justifyContent: 'flex-start' }} onClick={() => setShowArchived((v) => !v)}>{showArchived ? 'Hide' : 'Show'} archived ({archived.length})</Button>}
    </Stack>
    {wide && showArchived && archived.map((x) => <Stack key={x.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 0.5, fontSize: 12.5, color: MUTED }}>
      <span>{x.name}</span>{canManage && <Button size="small" onClick={() => run(() => updateAgency(x.id, (y) => ({ ...y, archived: false }), byName), `${x.name} restored`)}>Restore</Button>}
    </Stack>)}
  </Stack>;

  return <Box sx={{ maxWidth: 1240, mx: 'auto', width: '100%', p: { xs: 1.5, md: 3 } }}>
    <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1.25} sx={{ mb: 1.75 }}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: DARK }}>Agency SOPs</Typography>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>One standard SOP per agency in English and Gujarati. Project details like rates and retention are filled in when you make the PDF.</Typography>
      </Box>
      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDrawer({ kind: 'lesson' })}>Add point</Button>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

    <Box sx={{ display: 'grid', gridTemplateColumns: wide ? '240px minmax(0,1fr)' : 'minmax(0,1fr)', gap: wide ? 2.5 : 1.5, alignItems: 'start' }}>
      {rail}
      <Box sx={{ bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 3, minWidth: 0 }}>
        <Stack gap={1.75} sx={{ px: { xs: 2, sm: 2.75 }, pt: 2.5 }}>
          <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={1.5}>
            <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap" sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ fontFamily: lang === 'gu' && nameGu ? GU_FONT : HEAD_FONT, fontSize: { xs: 22, sm: 26 }, lineHeight: 1.2 }} lang={lang === 'gu' && nameGu ? 'gu' : undefined}>
                {lang === 'gu' && nameGu ? nameGu : isC ? 'Common for all agencies' : a.name}
                {lang === 'both' && nameGu && <Box component="span" lang="gu" sx={{ fontFamily: GU_FONT, fontSize: 17, color: MUTED, ml: 1.25 }}>{nameGu}</Box>}
              </Typography>
              {siteTeam
                ? <Box component="span" sx={{ ml: 1, ...tagSx(SUNK, MUTED) }}>Site team list</Box>
                : <Box component="span" sx={{ ml: 1, fontSize: 11.5, fontWeight: 700, color: GOLD_D, bgcolor: GOLD_SOFT, borderRadius: 1, px: 0.9 }}>v{a.version}</Box>}
              {!isC && canManage && <Button size="small" sx={{ color: MUTED }} onClick={() => setSettingsOpen((v) => !v)}>{settingsOpen ? 'Close settings' : siteTeam ? 'Settings' : 'Agency settings'}</Button>}
            </Stack>
            {!siteTeam && <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <ToggleButtonGroup size="small" exclusive value={lang} onChange={(_, v) => { if (v) { setLang(v); closeEdit(); } }} sx={segSx}>
                <ToggleButton value="en">English</ToggleButton>
                <ToggleButton value="gu" lang="gu" sx={{ fontFamily: GU_FONT }}>ગુજરાતી</ToggleButton>
                <ToggleButton value="both">Both</ToggleButton>
              </ToggleButtonGroup>
              {pdfButton}
            </Stack>}
          </Stack>
          {settingsRow}
          {!siteTeam && <Stack direction="row" flexWrap="wrap" sx={{ borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
            <Fact value={liveRows.length} label={isC ? 'common points' : 'agency points'} />
            {!isC && <Fact value={commonRows.length} label="common points" />}
            <Fact value={liveRows.filter((r) => r.hold).length} label="hold points" color={HOLD} />
            <Fact value={n} label="pending points" color={n ? HOLD : undefined} last />
          </Stack>}
          <Tabs value={siteTeam ? 'checklist' : tab} onChange={(_, v) => { setTab(v); closeEdit(); }} sx={{ minHeight: 40, mt: -1, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800, minHeight: 40, px: 0, mr: 2.75, minWidth: 0 } }}>
            {!siteTeam && <Tab value="sop" label="SOP" />}
            {!siteTeam && <Tab value="pending" label={<Stack direction="row" gap={0.75} alignItems="center">Pending points{n > 0 && <Box component="span" sx={tagSx(HOLD_SOFT, HOLD)}>{n}</Box>}</Stack>} />}
            {!siteTeam && (canManage || proposals.length > 0) && <Tab value="review" label={<Stack direction="row" gap={0.75} alignItems="center">AI review{openProposals > 0 && <Box component="span" sx={tagSx(BLUE_SOFT, BLUE)}>{openProposals}</Box>}</Stack>} />}
            {!siteTeam && <Tab value="placeholders" label={<Stack direction="row" gap={0.75} alignItems="center">Placeholders{issueCount > 0 && <Box component="span" sx={tagSx(WARN_SOFT, WARN)}>{issueCount}</Box>}</Stack>} />}
            <Tab value="checklist" label={<Stack direction="row" gap={0.75} alignItems="center">Start checklist{checkItems.length > 0 && checkStat.ready < checkStat.total && <Box component="span" sx={tagSx(WARN_SOFT, WARN)}>{checkStat.total - checkStat.ready}</Box>}</Stack>} />
          </Tabs>
        </Stack>
        <Box sx={{ px: { xs: 2, sm: 2.75 }, pb: 3, borderTop: `1px solid ${BORDER}` }}>{siteTeam || tab === 'checklist' ? checklistBody : tab === 'sop' ? sopBody : tab === 'pending' ? lessonsBody : tab === 'review' ? reviewBody : placeholdersBody}</Box>
      </Box>
    </Box>

    {drawer?.kind === 'point' && <AddPointDrawer pre={drawer.pre} agencies={agencyChoices} defaultAgency={siteTeam ? (common?.id || '') : a.id}
      lessons={lessons} onClose={() => setDrawer(null)} onSubmit={async (input) => { if (await addPoint(input)) setDrawer(null); }} />}
    {drawer?.kind === 'agency' && <AddAgencyDrawer agencies={agencies} active={active} defaultGroup={a.group} onClose={() => setDrawer(null)}
      onSubmit={async (input) => {
        const src = input.from ? agencies.find((x) => x.id === input.from) : null;
        const id = `${agencyKey(input.name) || 'agency'}-${Date.now().toString(36)}`;
        const rec: SopAgency = {
          id, name: input.name, nameGu: input.nameGu, order: Math.max(0, ...agencies.map((x) => x.order)) + 1, archived: false,
          projects: 'all', version: 1, ...(input.group ? { group: input.group } : {}),
          clauses: src ? src.clauses.map((p) => ({ ...p, id: newPointId() })) : [],
          log: [{ v: 1, note: src ? `Created from a copy of ${src.name}` : 'Created blank', at: new Date().toISOString(), by: byName }],
        };
        if (await run(() => createAgency(rec, byName), `${input.name} added${src ? ` with ${rec.clauses.length} points from ${src.name}` : ''}`)) { setDrawer(null); setAgencyId(id); setTab('sop'); }
      }} />}
    {drawer?.kind === 'lesson' && <LessonDrawer agencies={agencyChoices} defaultAgency={siteTeam ? (common?.id || '') : a.id}
      onClose={() => setDrawer(null)} onSubmit={async (input) => {
        const { agencyIds, ...rest } = input;
        const saved = await run(async () => {
          for (const id of agencyIds) await createLesson({ ...rest, agencyId: id, projectId: '', createdByName: byName }, currentUser?.uid || '');
        }, agencyIds.length > 1 ? `Saved as pending for ${agencyIds.length} agencies` : 'Saved as a pending point');
        if (saved) { setDrawer(null); setAgencyId(agencyIds[0]); setTab('pending'); setLessonFilter('new'); }
      }} />}
    {pdfOpen && <GeneratePdfDialog agency={a} common={isC ? null : common} projects={projects} defaultProjectId={currentProjectId || ''} pdfs={pdfs}
      byName={byName} uid={currentUser?.uid || ''} onClose={() => setPdfOpen(false)} onDone={(message) => { setPdfOpen(false); setSnack({ message }); }} />}

    <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} message={snack?.message}
      action={snack?.undo ? <Button size="small" sx={{ color: GOLD_SOFT }} onClick={() => { const u = snack?.undo; setSnack(null); if (u) run(u, 'Change undone'); }}>Undo</Button> : undefined} />
  </Box>;
}

const segSx = { bgcolor: SUNK, borderRadius: 2, p: 0.4, '& .MuiToggleButton-root': { border: 0, borderRadius: '7px !important', textTransform: 'none', fontWeight: 600, fontSize: 12.5, px: 1.5, py: 0.6, color: MUTED }, '& .Mui-selected': { bgcolor: '#fff !important', color: `${DARK} !important`, boxShadow: '0 1px 2px rgba(60,40,10,0.12)' } };

/** Point text with {{placeholders}} shown as small labelled chips; with `values`, the chips show those values instead. */
function PlaceholderText({ text, gu, values }: { text: string; gu?: boolean; values?: Record<string, string> }) {
  return <>{splitPlaceholders(text).map((part, i) => {
    if (!part.key) return <React.Fragment key={i}>{part.text}</React.Fragment>;
    const p = placeholderByKey(part.key);
    if (p && values) {
      const v = formatPlaceholderValue(part.key, values[part.key] || '') || BLANK_VALUE;
      return <Box key={i} component="span" title={`${p.label} (sample)`} sx={{ color: BLUE, fontWeight: 600, borderBottom: '1.5px dotted rgba(47,95,150,0.6)' }}>{v}</Box>;
    }
    return <Box key={i} component="span" title={part.text} sx={{ display: 'inline-block', px: 0.6, mx: 0.15, borderRadius: 1, fontSize: '0.86em', fontWeight: 600, lineHeight: 1.5, fontFamily: gu && p ? GU_FONT : undefined,
      bgcolor: p ? GOLD_SOFT : HOLD_SOFT, color: p ? GOLD_D : HOLD, border: `1px dashed ${p ? 'rgba(157,111,50,0.5)' : HOLD}` }}>{p ? (gu ? p.labelGu : p.label) : part.text}</Box>;
  })}</>;
}

/** Answer box for one checklist decision; saves when it loses focus. */
function DecisionField({ value, hint, onSave }: { value: string; hint?: string; onSave: (value: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return <TextField size="small" fullWidth value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v.trim() !== value.trim()) onSave(v.trim()); }}
    placeholder={hint ? `e.g. ${hint}` : 'Decided value'} />;
}

/** Edit the agency's checklist items: kind, English, Gujarati, example, order. */
function ChecklistEditor({ items, onSave, onCancel }: { items: SopCheckItem[]; onSave: (items: SopCheckItem[]) => void; onCancel: () => void }) {
  const [rows, setRows] = useState<SopCheckItem[]>(items.map((it) => ({ ...it })));
  const set = (i: number, patch: Partial<SopCheckItem>) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, dir: -1 | 1) => setRows((prev) => {
    const next = [...prev];
    const j = i + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const add = (kind: SopCheckItem['kind']) => setRows((prev) => [...prev, { id: newPointId(), kind, en: '', gu: '', hint: '' }]);
  const clean = rows.filter((r) => r.en.trim()).map((r) => {
    const out: SopCheckItem = { id: r.id, kind: r.kind, en: r.en.trim() };
    if (r.gu?.trim()) out.gu = r.gu.trim();
    if (r.kind === 'decision' && r.hint?.trim()) out.hint = r.hint.trim();
    return out;
  });
  return <Stack gap={1.25} sx={{ mt: 2 }}>
    <Typography sx={{ fontSize: 12.5, color: MUTED }}>The same items apply to every project; each project fills in its own answers. Removing an item hides its saved answers.</Typography>
    {rows.map((r, i) => <Box key={r.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '130px 1.4fr 1.4fr 1fr auto' }, gap: 1, alignItems: 'center', border: `1px solid ${BORDER}`, borderRadius: 2, p: 1 }}>
      <TextField select size="small" value={r.kind} onChange={(e) => set(i, { kind: e.target.value as SopCheckItem['kind'] })}>
        <MenuItem value="decision">Decision</MenuItem><MenuItem value="task">Task</MenuItem>
      </TextField>
      <TextField size="small" placeholder="Item in English" value={r.en} onChange={(e) => set(i, { en: e.target.value })} />
      <TextField size="small" placeholder="ગુજરાતી" value={r.gu || ''} onChange={(e) => set(i, { gu: e.target.value })} inputProps={{ lang: 'gu', style: { fontFamily: GU_FONT } }} />
      <TextField size="small" placeholder={r.kind === 'decision' ? 'Example answer' : '—'} disabled={r.kind !== 'decision'} value={r.kind === 'decision' ? r.hint || '' : ''} onChange={(e) => set(i, { hint: e.target.value })} />
      <Stack direction="row">
        <IconAction title="Move up" onClick={() => move(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconAction>
        <IconAction title="Move down" onClick={() => move(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconAction>
        <IconAction title="Remove" danger onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconAction>
      </Stack>
    </Box>)}
    <Stack direction="row" gap={1} flexWrap="wrap">
      <Button size="small" startIcon={<AddIcon />} onClick={() => add('decision')}>Add decision</Button>
      <Button size="small" startIcon={<AddIcon />} onClick={() => add('task')}>Add task</Button>
      <Box sx={{ flex: 1 }} />
      <Button size="small" sx={{ color: MUTED }} onClick={onCancel}>Cancel</Button>
      <Button size="small" variant="contained" onClick={() => onSave(clean)}>Save checklist</Button>
    </Stack>
  </Stack>;
}

/** English and Gujarati sample cells for one placeholder. Empty means "use the default"; saves when the field loses focus. */
function SampleEditor({ placeholderKey, agency, common, canEdit, onSave }: {
  placeholderKey: string; agency: SopAgency; common: SopAgency | null; canEdit: boolean; onSave: (next: SopSample) => void;
}) {
  const p = placeholderByKey(placeholderKey)!;
  const isCommon = agency.id === COMMON_SOP_ID;
  const own = agency.samples?.[placeholderKey] || {};
  // What applies when this SOP sets nothing: Common's sample for an agency, the built-in one for Common.
  const inherited = isCommon ? p.sample : sampleFor(placeholderKey, null, common);
  const resolved = sampleFor(placeholderKey, agency, common);
  const [en, setEn] = useState(own.en || '');
  const [gu, setGu] = useState(own.gu || '');
  const overridden = !!(own.en || own.gu);
  const save = () => { if (en.trim() !== (own.en || '') || gu.trim() !== (own.gu || '')) onSave({ en, gu }); };
  const caption = overridden ? (isCommon ? 'Default for all agencies' : `${agency.name} only`) : isCommon ? 'Built-in example' : 'From Common';
  if (!canEdit) {
    return <>
      <Typography sx={{ fontSize: 13 }}>{formatPlaceholderValue(placeholderKey, resolved.en)}</Typography>
      <Typography lang="gu" sx={{ fontSize: 13, fontFamily: GU_FONT }}>{p.translate ? resolved.gu : formatPlaceholderValue(placeholderKey, resolved.en)}</Typography>
    </>;
  }
  return <>
    <Box>
      <TextField size="small" fullWidth value={en} onChange={(e) => setEn(e.target.value)} onBlur={save} type={p.kind === 'date' ? 'date' : 'text'}
        placeholder={inherited.en} multiline={p.kind === 'longtext'} inputProps={{ 'aria-label': `${p.label} sample in English` }} />
      <Stack direction="row" gap={1} alignItems="center" sx={{ mt: 0.4 }}>
        <Typography sx={{ fontSize: 11, color: overridden ? GOLD_D : FAINT, fontWeight: overridden ? 700 : 500 }}>{caption}</Typography>
        {overridden && <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: 11, color: MUTED }} onClick={() => { setEn(''); setGu(''); onSave({}); }}>Reset</Button>}
      </Stack>
    </Box>
    {p.translate
      ? <TextField size="small" fullWidth value={gu} onChange={(e) => setGu(e.target.value)} onBlur={save} placeholder={inherited.gu} multiline={p.kind === 'longtext'}
        inputProps={{ lang: 'gu', style: { fontFamily: GU_FONT }, 'aria-label': `${p.label} sample in Gujarati` }} />
      : <Typography sx={{ fontSize: 12, color: FAINT, pt: 1 }}>Same as English</Typography>}
  </>;
}

/** A point text box with an "Insert placeholder" menu that puts {{key}} where the cursor is. */
function SopTextField({ label, value, onChange, gu, autoFocus, placeholder, extra, minRows = 2 }: {
  label: string; value: string; onChange: (v: string) => void; gu?: boolean; autoFocus?: boolean; placeholder?: string; extra?: React.ReactNode; minRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const el = ref.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    setAnchor(null);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } });
  };
  const groups = Array.from(new Set(SOP_PLACEHOLDERS.map((p) => p.group)));
  return <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
      <Typography lang={gu ? 'gu' : undefined} sx={{ fontSize: 12, fontWeight: 600, color: MUTED, fontFamily: gu ? GU_FONT : undefined }}>{label}</Typography>
      <Stack direction="row" gap={0.5} alignItems="center">
        {extra}
        <Button size="small" startIcon={<DataObjectOutlinedIcon />} onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: GOLD_D }}>Insert placeholder</Button>
      </Stack>
    </Stack>
    <TextField value={value} onChange={(e) => onChange(e.target.value)} multiline minRows={minRows} size="small" fullWidth autoFocus={autoFocus} placeholder={placeholder}
      inputRef={ref} inputProps={gu ? { lang: 'gu', style: { fontFamily: GU_FONT } } : undefined} sx={{ bgcolor: '#fff' }} />
    {/{{/.test(value) && <Typography component="div" sx={{ fontSize: 12.5, color: MUTED, mt: 0.75, fontFamily: gu ? GU_FONT : undefined }}>Shows as: <PlaceholderText text={value} gu={gu} /></Typography>}
    <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { maxHeight: 380 } }}>
      {groups.flatMap((g) => [
        <MenuItem key={`h-${g}`} disabled sx={{ fontSize: 11.5, fontWeight: 700, opacity: '1 !important', color: MUTED }}>{g}</MenuItem>,
        ...SOP_PLACEHOLDERS.filter((p) => p.group === g).map((p) => <MenuItem key={p.key} onClick={() => insert(p.key)} sx={{ fontSize: 13, pl: 3 }}>{p.label}<Box component="span" sx={{ color: FAINT, ml: 1, fontSize: 11.5 }}>{`{{${p.key}}}`}</Box></MenuItem>),
      ])}
    </Menu>
  </Box>;
}

function TagRow({ children }: { children: React.ReactNode }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return items.length ? <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.6 }}>{items}</Stack> : null;
}

function IconAction({ title, onClick, danger, children }: { title: string; onClick: () => void; danger?: boolean; children: React.ReactNode }) {
  return <Tooltip title={title}><IconButton size="small" aria-label={title} onClick={onClick} sx={{ color: FAINT, '&:hover': { color: danger ? HOLD : DARK, bgcolor: SUNK } }}>{children}</IconButton></Tooltip>;
}

function AiButton({ label, busy, onClick, outlined }: { label: string; busy: boolean; onClick: () => void; outlined?: boolean }) {
  return <Button size="small" variant={outlined ? 'outlined' : 'text'} disabled={busy} onClick={onClick}
    startIcon={busy ? <CircularProgress size={13} /> : <AutoAwesomeOutlinedIcon />} sx={{ color: BLUE, borderColor: 'rgba(47,95,150,0.4)', '&:hover': { bgcolor: BLUE_SOFT, borderColor: BLUE } }}>
    {busy ? 'Working…' : label}
  </Button>;
}

function Hint({ title, sub }: { title: string; sub: string }) {
  return <Box><Typography sx={{ fontSize: 13 }}>{title}</Typography><Typography sx={{ fontSize: 12, color: MUTED }}>{sub}</Typography></Box>;
}

function Banner({ children, grey }: { children: React.ReactNode; grey?: boolean }) {
  return <Stack direction="row" gap={1.25} alignItems="flex-start" sx={{ bgcolor: grey ? SUNK : GOLD_SOFT, borderRadius: 2.5, px: 1.75, py: 1.4, mt: 2, fontSize: 12.5 }}>
    <InfoOutlinedIcon sx={{ fontSize: 17, color: grey ? MUTED : GOLD_D, mt: '1px' }} /><Box>{children}</Box>
  </Stack>;
}

function Fact({ value, label, color, last }: { value: number; label: string; color?: string; last?: boolean }) {
  return <Box sx={{ py: 1.25, pr: 2.25, mr: last ? 0 : 2.25, borderRight: last ? 0 : `1px solid ${BORDER}` }}>
    <Typography sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2, color: color || DARK, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    <Typography sx={{ fontSize: 11.5, color: MUTED }}>{label}</Typography>
  </Box>;
}

function DrawerShell({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) {
  return <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}` }}>
      <Typography sx={{ fontFamily: HEAD_FONT, fontSize: 20 }}>{title}</Typography>
      <IconButton aria-label="Close" onClick={onClose}><CloseIcon /></IconButton>
    </Stack>
    <Stack gap={1.75} sx={{ px: 2.5, py: 2.25, overflowY: 'auto', flex: 1 }}>{children}</Stack>
    <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ px: 2.5, py: 1.75, borderTop: `1px solid ${BORDER}` }}>{footer}</Stack>
  </Drawer>;
}

function AgencySettings({ agency, index, count, onSaveNames, onMove, onArchive, onGroup }: {
  agency: SopAgency; index: number; count: number;
  onSaveNames: (name: string, nameGu: string) => void; onMove: (dir: -1 | 1) => void; onArchive: () => void; onGroup: (group: string) => void;
}) {
  const [name, setName] = useState(agency.name);
  const [nameGu, setNameGu] = useState(agency.nameGu || '');
  return <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ sm: 'center' }} sx={{ bgcolor: SUNK, borderRadius: 2.5, p: 1.5 }}>
    <TextField size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1, bgcolor: '#fff' }} />
    <TextField size="small" label="ગુજરાતી નામ" value={nameGu} onChange={(e) => setNameGu(e.target.value)} sx={{ flex: 1, bgcolor: '#fff' }} inputProps={{ lang: 'gu', style: { fontFamily: GU_FONT } }} />
    <Button variant="outlined" size="small" disabled={!name.trim()} onClick={() => onSaveNames(name.trim(), nameGu.trim())}>Save names</Button>
    <TextField select size="small" label="Group" value={agency.group || ''} onChange={(e) => onGroup(String(e.target.value))} sx={{ minWidth: 170, bgcolor: '#fff' }}
      SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
      <MenuItem value="">Other</MenuItem>
      {SOP_GROUPS.map((g) => <MenuItem key={g.key} value={g.key}>{g.label}</MenuItem>)}
    </TextField>
    <Stack direction="row" alignItems="center"><IconAction title="Move up" onClick={() => onMove(-1)}><ArrowUpwardIcon fontSize="small" /></IconAction><IconAction title="Move down" onClick={() => onMove(1)}><ArrowDownwardIcon fontSize="small" /></IconAction>
      <Typography sx={{ fontSize: 11.5, color: FAINT, mx: 1 }}>{index + 1} of {count}</Typography></Stack>
    <Button size="small" color="error" onClick={onArchive}>Archive</Button>
  </Stack>;
}

type AddPointInput = { targets: string[]; section: number; after: string; en: string; gu: string; guok: boolean; hold: boolean; lessonId?: string };

function AddPointDrawer({ pre, agencies, defaultAgency, lessons, onClose, onSubmit }: {
  pre: AddPre; agencies: SopAgency[]; defaultAgency: string; lessons: SopLesson[]; onClose: () => void; onSubmit: (input: AddPointInput) => Promise<void>;
}) {
  const [targets, setTargets] = useState<string[]>(pre.targets || [defaultAgency]);
  const [section, setSection] = useState(pre.section ?? 0);
  const [after, setAfter] = useState(pre.after || '');
  const [en, setEn] = useState(pre.en || '');
  const [gu, setGu] = useState(pre.gu || '');
  const [guok, setGuok] = useState(false);
  const [hold, setHold] = useState(!!pre.hold);
  const [pointType, setPointType] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const lesson = pre.lessonId ? lessons.find((l) => l.id === pre.lessonId) : undefined;
  const common = targets.includes(COMMON_SOP_ID);
  const byId = (id: string) => agencies.find((x) => x.id === id);

  const positionRows = useMemo(() => {
    if (targets.length !== 1) return [];
    const t = agencies.find((x) => x.id === targets[0]);
    return t ? sectionRows({ ...t, clauses: workingClauses(t) }, null, section) : [];
  }, [targets, section, agencies]);
  useEffect(() => { if (after && !positionRows.some((r) => r.id === after)) setAfter(''); }, [after, positionRows]);

  const toggle = (id: string) => {
    if (id === COMMON_SOP_ID) { setTargets(common ? [defaultAgency === COMMON_SOP_ID ? '' : defaultAgency].filter(Boolean) : [COMMON_SOP_ID]); return; }
    setTargets((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev.filter((x) => x !== COMMON_SOP_ID), id]));
  };
  const ai = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setErr('');
    try { await fn(); } catch (e: any) { setErr(translateErrorMessage(e)); } finally { setBusy(''); }
  };
  const names = common ? 'Common for all agencies' : targets.map((t) => byId(t)?.name || t).join(', ');
  const one = targets.length === 1 ? byId(targets[0]) : undefined;
  const guLine = gu.trim() ? (guok ? 'Gujarati saved as checked.' : 'Gujarati saved as an AI draft to check.') : 'No Gujarati yet; it shows as "not translated".';

  const submit = async () => {
    if (!targets.length) { setErr('Pick at least one agency.'); return; }
    if (!en.trim()) { setErr(gu.trim() ? 'English is the master text. Use "From Gujarati" to fill it, then check it.' : 'Write the point in English.'); return; }
    setSaving(true);
    await onSubmit({ targets, section, after: targets.length === 1 ? after : '', en: en.trim(), gu: gu.trim(), guok, hold, lessonId: pre.lessonId });
    setSaving(false);
  };

  return <DrawerShell title="Add to draft" onClose={onClose} footer={<><Button sx={{ color: MUTED }} onClick={onClose}>Cancel</Button><Button variant="contained" disabled={saving} onClick={submit}>{saving ? 'Adding…' : 'Add to draft'}</Button></>}>
    {lesson && <Stack gap={0.75} sx={{ bgcolor: SUNK, borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: MUTED }}>From pending point</Typography>
      <Typography lang={hasGujaratiScript(lesson.suggest || lesson.what) ? 'gu' : undefined} sx={{ fontSize: 13, fontFamily: hasGujaratiScript(lesson.suggest || lesson.what) ? GU_FONT : undefined }}>{shortText(lesson.suggest || lesson.what, 220)}</Typography>
      <Box><AiButton busy={busy === 'formal'} label="Write it as an SOP point with AI" onClick={() => ai('formal', async () => {
        const out = await formalizeLesson(byId(lesson.agencyId)?.name || 'agency', lesson.what, lesson.suggest);
        setEn(out.en); setGu(out.gu); setGuok(false); setSection(out.section); setHold(out.hold); setPointType(out.type);
      })} /></Box>
    </Stack>}
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, mb: 0.75 }}>1. Agency</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {agencies.map((x) => {
          const on = targets.includes(x.id);
          const xc = x.id === COMMON_SOP_ID;
          return <Chip key={x.id} label={xc ? 'All agencies (Common)' : x.name} icon={xc ? <LayersOutlinedIcon /> : undefined} onClick={() => toggle(x.id)} disabled={common && !xc}
            variant={on ? 'filled' : 'outlined'} sx={{ fontWeight: 600, borderStyle: xc ? 'dashed' : 'solid', bgcolor: on ? GOLD_SOFT : '#fff', color: on ? GOLD_D : DARK, borderColor: on ? GOLD : BORDER }} />;
        })}
      </Stack>
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
      <TextField select size="small" label="2. Section" value={section} onChange={(e) => setSection(Number(e.target.value))} fullWidth>
        {SOP_SECTIONS.map((s, i) => <MenuItem key={s} value={i}>{s}</MenuItem>)}
      </TextField>
      <TextField select size="small" label="3. Position" value={targets.length === 1 ? after : ''} onChange={(e) => setAfter(String(e.target.value))} disabled={targets.length !== 1} fullWidth
        SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
        <MenuItem value="">{targets.length === 1 ? 'At the end of the section' : targets.length ? 'At the end in each agency' : 'Pick an agency first'}</MenuItem>
        {positionRows.map((r) => <MenuItem key={r.id} value={r.id}>After {r.num}: {shortText(r.text, 40)}</MenuItem>)}
      </TextField>
    </Stack>
    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ bgcolor: BLUE_SOFT, borderRadius: 2, px: 1.25, py: 0.75 }}>
      <AiButton busy={busy === 'rewrite'} label="Rewrite in SOP style" onClick={() => {
        if (!en.trim() && !gu.trim()) { setErr('Write the point first, in your own words, in English or Gujarati.'); return; }
        ai('rewrite', async () => {
          const out = await rewriteInSopStyle(byId(targets[0])?.name || 'agency', en.trim(), gu.trim());
          setEn(out.en); setGu(out.gu); setGuok(false); setSection(out.section); setHold(out.hold); setPointType(out.type);
        });
      }} />
      {pointType && (SOP_POINT_TYPES as Record<string, string>)[pointType]
        ? <Box component="span" sx={tagSx('#fff', GOLD_D, 'rgba(157,111,50,0.35)')}>{(SOP_POINT_TYPES as Record<string, string>)[pointType]}</Box>
        : <Typography sx={{ fontSize: 12, color: MUTED }}>Type it your way, then let AI write it in the standard SOP pattern with Gujarati.</Typography>}
    </Stack>
    <SopTextField label="4. Point in English (master)" value={en} onChange={(v) => { setEn(v); setPointType(''); }} autoFocus={!pre.gu} placeholder="Write it as an instruction the agency can follow"
      extra={<AiButton busy={busy === 'toEn'} label="From Gujarati" onClick={() => { if (!gu.trim()) { setErr('Write the Gujarati first.'); return; } ai('toEn', async () => { const [out] = await translateTexts([gu.trim()], 'en'); setEn(out); }); }} />} />
    <SopTextField label="ગુજરાતી" gu value={gu} onChange={(v) => { setGu(v); setGuok(false); }} placeholder="Optional. Type it, or use AI translate and correct it"
      extra={<AiButton busy={busy === 'toGu'} label="AI translate" onClick={() => { if (!en.trim()) { setErr('Write the English point first.'); return; } ai('toGu', async () => { const [out] = await translateTexts([en.trim()], 'gu'); setGu(out); setGuok(false); }); }} />} />
    <FormControlLabel control={<Checkbox checked={guok} onChange={(e) => setGuok(e.target.checked)} disabled={!gu.trim()} />} label={<Hint title="I checked the Gujarati" sub={'If not ticked, it is saved as an AI draft and shows "check it".'} />} />
    <FormControlLabel control={<Checkbox checked={hold} onChange={(e) => setHold(e.target.checked)} sx={{ '&.Mui-checked': { color: HOLD } }} />} label={<Hint title="Hold point" sub="Work stops here until the engineer signs." />} />
    <Box sx={{ bgcolor: SUNK, borderRadius: 2, px: 1.5, py: 1.25, fontSize: 12.5 }}>
      {targets.length
        ? <>Adds to <b>{names}</b> in {SOP_SECTIONS[section]}. {one ? `It goes into draft v${one.version + 1}` : 'It goes into each draft'}; PDFs get it after you publish. {guLine}</>
        : <span style={{ color: HOLD, fontWeight: 600 }}>Pick at least one agency.</span>}
    </Box>
    {err && <Alert severity="warning" onClose={() => setErr('')}>{err}</Alert>}
  </DrawerShell>;
}

function AddAgencyDrawer({ agencies, active, onClose, onSubmit, defaultGroup }: {
  agencies: SopAgency[]; active: SopAgency[]; onClose: () => void;
  onSubmit: (input: { name: string; nameGu: string; from: string; group: string }) => Promise<void>;
  defaultGroup?: string;
}) {
  const [name, setName] = useState('');
  const [nameGu, setNameGu] = useState('');
  const [from, setFrom] = useState('');
  const [group, setGroup] = useState(defaultGroup && defaultGroup !== 'setup' ? defaultGroup : '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (!clean) { setErr('Enter the agency name.'); return; }
    const clash = agencies.find((x) => x.id !== COMMON_SOP_ID && agencyKey(x.name) === agencyKey(clean));
    if (clash) { setErr(clash.archived ? `${clash.name} already exists in the archive. Restore it instead.` : `${clash.name} already exists.`); return; }
    setSaving(true);
    await onSubmit({ name: clean, nameGu: nameGu.trim(), from, group });
    setSaving(false);
  };
  return <DrawerShell title="Add agency" onClose={onClose} footer={<><Button sx={{ color: MUTED }} onClick={onClose}>Cancel</Button><Button variant="contained" disabled={saving} onClick={submit}>Add agency</Button></>}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
      <TextField size="small" label="Name in English" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Waterproofing" autoFocus fullWidth />
      <TextField size="small" label="ગુજરાતી નામ" value={nameGu} onChange={(e) => setNameGu(e.target.value)} placeholder="e.g. વોટરપ્રૂફિંગ" fullWidth inputProps={{ lang: 'gu', style: { fontFamily: GU_FONT } }} />
    </Stack>
    <TextField select size="small" label="Start from" value={from} onChange={(e) => setFrom(String(e.target.value))} fullWidth SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
      <MenuItem value="">Blank. Only the Common points apply until you add more</MenuItem>
      {active.filter((x) => !x.siteTeam).map((x) => <MenuItem key={x.id} value={x.id}>Copy of {x.name} ({x.clauses.length} points, Gujarati included)</MenuItem>)}
    </TextField>
    <TextField select size="small" label="Group" value={group} onChange={(e) => setGroup(String(e.target.value))} fullWidth SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
      <MenuItem value="">Other</MenuItem>
      {SOP_GROUPS.filter((g) => g.key !== 'setup').map((g) => <MenuItem key={g.key} value={g.key}>{g.label}</MenuItem>)}
    </TextField>
    <Typography sx={{ fontSize: 12, color: MUTED }}>It also appears in Change Orders, so both screens use the same name.</Typography>
    {err && <Alert severity="warning">{err}</Alert>}
  </DrawerShell>;
}

function LessonDrawer({ agencies, defaultAgency, onClose, onSubmit }: {
  agencies: SopAgency[]; defaultAgency: string; onClose: () => void;
  onSubmit: (input: { agencyIds: string[]; impact: SopImpact; what: string; suggest: string }) => Promise<void>;
}) {
  const [agencyIds, setAgencyIds] = useState<string[]>([defaultAgency]);
  const [impact, setImpact] = useState<SopImpact>('rework');
  const [what, setWhat] = useState('');
  const [suggest, setSuggest] = useState('');
  const [saving, setSaving] = useState(false);
  const common = agencyIds.includes(COMMON_SOP_ID);
  const ready = !!(what.trim() || suggest.trim()) && agencyIds.length > 0;
  // "All agencies" stands alone: it goes to the Common SOP instead of one copy per agency.
  const toggle = (id: string) => {
    if (id === COMMON_SOP_ID) { setAgencyIds(common ? [] : [COMMON_SOP_ID]); return; }
    setAgencyIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev.filter((x) => x !== COMMON_SOP_ID), id]));
  };
  const submit = async () => {
    if (!ready) return;
    setSaving(true);
    await onSubmit({ agencyIds, impact, what: what.trim(), suggest: suggest.trim() });
    setSaving(false);
  };
  const gu = { lang: 'gu', style: { fontFamily: GU_FONT } };
  return <DrawerShell title="Add point" onClose={onClose} footer={<><Button sx={{ color: MUTED }} onClick={onClose}>Cancel</Button><Button variant="contained" disabled={saving || !ready} onClick={submit}>Save as pending</Button></>}>
    <Typography sx={{ fontSize: 12.5, color: MUTED }}>Write in any language (English, Gujarati or Hinglish), no formatting needed. It is saved exactly as you write it as a <b>pending point</b>, and becomes a proper SOP point when the next version is drafted and reviewed.</Typography>
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: MUTED, mb: 0.75 }}>Agency (pick one or more)</Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {agencies.map((x) => {
          const on = agencyIds.includes(x.id);
          const xc = x.id === COMMON_SOP_ID;
          return <Chip key={x.id} label={xc ? 'All agencies (Common)' : x.name} icon={xc ? <LayersOutlinedIcon /> : undefined} onClick={() => toggle(x.id)} disabled={common && !xc}
            variant={on ? 'filled' : 'outlined'} sx={{ fontWeight: 600, borderStyle: xc ? 'dashed' : 'solid', bgcolor: on ? GOLD_SOFT : '#fff', color: on ? GOLD_D : DARK, borderColor: on ? GOLD : BORDER }} />;
        })}
      </Stack>
      {agencyIds.length > 1 && <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>Saved as a pending point for each of the {agencyIds.length} agencies.</Typography>}
    </Box>
    <TextField select size="small" label="Impact" value={impact} onChange={(e) => setImpact(e.target.value as SopImpact)} fullWidth>
      {SOP_IMPACTS.map((x) => <MenuItem key={x.value} value={x.value}>{x.label}</MenuItem>)}
    </TextField>
    <TextField label="Point or idea" value={suggest} onChange={(e) => setSuggest(e.target.value)} multiline minRows={3} fullWidth autoFocus placeholder="What should agencies do? Write it your way, in any language." inputProps={gu} />
    <TextField label="What happened (optional)" value={what} onChange={(e) => setWhat(e.target.value)} multiline minRows={2} fullWidth placeholder="Flat, area, and what went wrong or worked well" inputProps={gu} />
  </DrawerShell>;
}

const KIND_LABEL: Record<SopProposal['kind'], { label: string; bg: string; color: string }> = {
  new: { label: 'New point', bg: GREEN_SOFT, color: GREEN },
  reword: { label: 'Improve a point', bg: BLUE_SOFT, color: BLUE },
  duplicate: { label: 'Already covered', bg: SUNK, color: MUTED },
  vendor: { label: 'Vendor note, not a rule', bg: GOLD_SOFT, color: GOLD_D },
};

/** One AI proposal: shows where it came from, lets the reviewer edit it, then accept or reject. */
function ProposalCard({ proposal, sources, target, canDecide, onDecide }: {
  proposal: SopProposal; sources: SopLesson[]; target?: SopRow; canDecide: boolean;
  onDecide: (edited: SopProposal, accept: boolean, guChecked: boolean) => Promise<void>;
}) {
  const [en, setEn] = useState(proposal.en);
  const [gu, setGu] = useState(proposal.gu);
  const [section, setSection] = useState(proposal.section);
  const [hold, setHold] = useState(proposal.hold);
  const [guChecked, setGuChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const kind = KIND_LABEL[proposal.kind];
  const writes = proposal.kind === 'new' || proposal.kind === 'reword';
  const open = proposal.status === 'open';
  const decide = async (accept: boolean) => {
    setSaving(true);
    await onDecide({ ...proposal, en: en.trim(), gu: gu.trim(), section, hold }, accept, guChecked);
    setSaving(false);
  };
  return <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 1.75, opacity: open ? 1 : 0.6, bgcolor: '#fff' }}>
    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
      <Box component="span" sx={tagSx(kind.bg, kind.color)}>{kind.label}</Box>
      {target && <Typography sx={{ fontSize: 12, color: MUTED }}>{proposal.kind === 'duplicate' ? 'Covered by' : 'Point'} {target.num}</Typography>}
      {proposal.conflict && <Box component="span" sx={tagSx(HOLD, '#fff')}>Conflict: you decide</Box>}
      {proposal.pointType && SOP_POINT_TYPES[proposal.pointType] &&<Box component="span" sx={tagSx('#fff', proposal.pointType === 'hold' ? HOLD : GOLD_D, proposal.pointType === 'hold' ? 'rgba(179,67,43,0.35)' : 'rgba(157,111,50,0.35)')}>{SOP_POINT_TYPES[proposal.pointType]}</Box>}
      {!open && <Box component="span" sx={tagSx(proposal.status === 'accepted' ? GREEN_SOFT : SUNK, proposal.status === 'accepted' ? GREEN : MUTED)}>{proposal.status === 'accepted' ? 'Accepted' : 'Rejected'}</Box>}
    </Stack>
    {proposal.reason && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.75 }}>{proposal.reason}</Typography>}
    {open && !!proposal.removedPlaceholders?.length && <Stack direction="row" gap={0.75} alignItems="flex-start" sx={{ mt: 1, bgcolor: WARN_SOFT, color: WARN, borderRadius: 2, px: 1.25, py: 0.9, fontSize: 12.5 }}>
      <WarningAmberOutlinedIcon sx={{ fontSize: 16, mt: '1px' }} />
      <span>AI used {proposal.removedPlaceholders.map((k) => placeholderByKey(k)?.label || k).join(', ')}, which only belongs in the PDF header. It was replaced with plain words. Check the wording before you accept.</span>
    </Stack>}
    {proposal.source ? <Box sx={{ mt: 1, bgcolor: SUNK, borderRadius: 2, px: 1.5, py: 1 }}>
      <Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>From old SOP PDF{proposal.source.label ? `, ${proposal.source.label}` : ''}</Typography>
      <Typography lang={hasGujaratiScript(proposal.source.text) ? 'gu' : undefined} sx={{ fontSize: 12.5, mt: 0.5, whiteSpace: 'pre-line', fontFamily: hasGujaratiScript(proposal.source.text) ? GU_FONT : undefined }}>“{proposal.source.text}”</Typography>
    </Box> : <Box sx={{ mt: 1, bgcolor: SUNK, borderRadius: 2, px: 1.5, py: 1 }}>
      <Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>From {sources.length} pending point{sources.length === 1 ? '' : 's'}</Typography>
      {sources.map((s) => {
        const text = s.suggest || s.what;
        return <Typography key={s.id} lang={hasGujaratiScript(text) ? 'gu' : undefined} sx={{ fontSize: 12.5, mt: 0.5, fontFamily: hasGujaratiScript(text) ? GU_FONT : undefined }}>“{shortText(text, 200)}”{s.createdByName ? <Box component="span" sx={{ color: FAINT }}> {s.createdByName}</Box> : null}</Typography>;
      })}
    </Box>}
    {target && proposal.kind !== 'vendor' && <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>{proposal.kind === 'reword' ? 'Current point' : 'Existing point'}</Typography>
      <Typography sx={{ fontSize: 13, textDecoration: proposal.kind === 'reword' && open ? 'line-through' : 'none', color: proposal.kind === 'reword' ? FAINT : DARK }}><PlaceholderText text={target.text} /></Typography>
    </Box>}
    {writes && (open && canDecide ? <Stack gap={1.25} sx={{ mt: 1.5 }}>
      <SopTextField label="English" value={en} onChange={setEn} />
      <SopTextField label="ગુજરાતી" gu value={gu} onChange={(v) => { setGu(v); setGuChecked(false); }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ sm: 'center' }}>
        <TextField select size="small" label="Section" value={section} onChange={(e) => setSection(Number(e.target.value))} sx={{ minWidth: 200 }}>
          {SOP_SECTIONS.map((s, i) => <MenuItem key={s} value={i}>{s}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Checkbox checked={hold} onChange={(e) => setHold(e.target.checked)} sx={{ '&.Mui-checked': { color: HOLD } }} />} label="Hold point" />
        <FormControlLabel control={<Checkbox checked={guChecked} onChange={(e) => setGuChecked(e.target.checked)} disabled={!gu.trim()} />} label="I checked the Gujarati" />
      </Stack>
    </Stack> : <Stack gap={0.5} sx={{ mt: 1.25 }}>
      <Typography sx={{ fontSize: 13.5 }}><PlaceholderText text={en} /></Typography>
      {gu && <Typography lang="gu" sx={{ fontSize: 13.5, fontFamily: GU_FONT, color: '#3d3a35' }}><PlaceholderText text={gu} gu /></Typography>}
    </Stack>)}
    {open && canDecide && <Stack direction="row" gap={1} sx={{ mt: 1.5 }}>
      <Button variant="contained" size="small" disabled={saving || (writes && !en.trim())} onClick={() => decide(true)}>
        {proposal.kind === 'new' ? 'Accept into draft' : proposal.kind === 'reword' ? 'Accept change' : proposal.kind === 'duplicate' ? 'Agree, already covered' : 'Agree, vendor note'}
      </Button>
      <Button size="small" sx={{ color: MUTED }} disabled={saving} onClick={() => decide(false)}>Reject</Button>
    </Stack>}
  </Box>;
}

/** Ask for the project details the SOP needs, then make the bilingual PDF on the project's letterhead. */
function GeneratePdfDialog({ agency, common, projects, defaultProjectId, pdfs, byName, uid, onClose, onDone }: {
  agency: SopAgency; common: SopAgency | null; projects: ProjectRef[]; defaultProjectId: string; pdfs: SopPdfRecord[];
  byName: string; uid: string; onClose: () => void; onDone: (message: string) => void;
}) {
  const [projectId, setProjectId] = useState(defaultProjectId || projects[0]?.id || '');
  const [en, setEn] = useState<Record<string, string>>({});
  const [gu, setGu] = useState<Record<string, string>>({});
  const [rates, setRates] = useState<SopRateRow[]>([{ item: '', unit: '', rate: '' }]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [filledFrom, setFilledFrom] = useState('');
  const [approved, setApproved] = useState<Array<{ label: string; labelGu: string; value: string }>>([]);
  const [openTasks, setOpenTasks] = useState<string[]>([]);
  // The project's start checklist: decisions print as "Approved materials"; unfinished tasks only warn.
  useEffect(() => {
    let live = true;
    if (!projectId) { setApproved([]); setOpenTasks([]); return undefined; }
    Promise.all([loadChecklistAnswers(projectId, agency.id), common ? loadChecklistAnswers(projectId, common.id) : Promise.resolve({})])
      .then(([own, com]) => {
        if (!live) return;
        const sources = [{ items: agency.checklist, answers: own }, ...(common ? [{ items: common.checklist, answers: com }] : [])];
        setApproved(approvedMaterials(sources));
        setOpenTasks(sources.flatMap((s) => checklistStatus(s.items, s.answers).openTasks.map((t) => t.en)));
      })
      .catch(() => { if (live) { setApproved([]); setOpenTasks([]); } });
    return () => { live = false; };
  }, [projectId, agency, common]);
  const keys = useMemo(() => pdfPlaceholderKeys({ agency, common }), [agency, common]);
  const fields = keys.map((k) => placeholderByKey(k)!).filter(Boolean);
  const groups = Array.from(new Set(fields.map((f) => f.group)));
  const projectName = projects.find((p) => p.id === projectId)?.name || '';

  // Start from the last PDF for this agency and project, else the last one for this agency.
  useEffect(() => {
    const prev = pdfs.find((p) => p.projectId === projectId && p.values) || pdfs.find((p) => p.values);
    if (!prev?.values) { setFilledFrom(''); return; }
    const sameProject = prev.projectId === projectId;
    const copy = { ...prev.values.en };
    if (!sameProject) delete copy.work_order_no;
    setEn(copy);
    setGu({ ...prev.values.gu });
    if (prev.rates?.length) setRates(prev.rates.map((r) => ({ ...r })));
    setFilledFrom(`${sameProject ? 'Filled in' : 'Started'} from the PDF of ${new Date(prev.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}${sameProject ? '' : ` for ${prev.projectName}`}. Check every value.`);
  }, [projectId, pdfs]);

  const translatable = fields.filter((f) => f.translate);
  const translateAll = async () => {
    setBusy('tr'); setErr('');
    try {
      const textJobs = translatable.filter((f) => (en[f.key] || '').trim() && !(gu[f.key] || '').trim());
      const rateJobs = rates.map((r, i) => ({ r, i })).filter(({ r }) => r.item.trim() && !(r.itemGu || '').trim());
      const texts = [...textJobs.map((f) => en[f.key].trim()), ...rateJobs.map(({ r }) => r.item.trim())];
      if (!texts.length) { setErr('Nothing new to translate.'); return; }
      const out = await translateTexts(texts, 'gu');
      const nextGu = { ...gu };
      textJobs.forEach((f, i) => { nextGu[f.key] = out[i]; });
      setGu(nextGu);
      setRates((prev) => prev.map((r, i) => {
        const j = rateJobs.findIndex((x) => x.i === i);
        return j >= 0 ? { ...r, itemGu: out[textJobs.length + j] } : r;
      }));
    } catch (e: any) { setErr(translateErrorMessage(e)); } finally { setBusy(''); }
  };

  const generate = async () => {
    if (!projectId) { setErr('Pick the project.'); return; }
    setBusy('pdf'); setErr('');
    try {
      const { template, project, company } = await loadSopPdfContext(projectId);
      const issueDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
      const values = { en: trimAll(en), gu: trimAll(gu) };
      const cleanRates = rates.filter((r) => r.item.trim() || r.rate.trim()).map((r) => ({ item: r.item.trim(), itemGu: (r.itemGu || '').trim(), unit: r.unit.trim(), rate: r.rate.trim() }));
      const body = buildSopPdfBody({ agency, common, projectName, values, rates: cleanRates, approved, issuedOn: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }), issuedBy: byName });
      const tokens: Record<string, string> = {
        '{{issue_date}}': issueDate,
        '{{ref_no}}': values.en.work_order_no || `SOP/${agency.name.slice(0, 3).toUpperCase()}/v${agency.version}`,
        '{{company_name}}': String(company?.name || ''),
        '{{company_address}}': String(company?.address || ''),
        '{{company_mobile}}': String(company?.mobile || ''),
        '{{company_email}}': String(company?.email || ''),
        '{{project_name}}': projectName,
        '{{agency_name}}': agency.name,
        '{{agency_firm}}': values.en.agency_firm || '',
        '{{sop_version}}': `v${agency.version}`,
      };
      const templateHtml = String(template?.renderHtml || template?.visualModel?.renderHtml || template?.visualModel?.body || template?.bodyHtml || '');
      const html = /\{\{\s*sop_content\s*\}\}/.test(templateHtml)
        ? applyTemplateTokens(templateHtml, tokens).replace(/\{\{\s*sop_content\s*\}\}/g, body)
        : body;
      const vm = template?.visualModel || { showPageNumbers: true, companyLogoHeightCm: 1.4, projectLogoHeightCm: 1.4, marginTopCm: 0.6, marginBottomCm: 1, marginLeftCm: 1.4, marginRightCm: 1.2, footerHeightCm: 0.8 };
      const prefersBw = Boolean(template?.useProjectLogoBw || vm.useProjectLogoBw);
      const fileName = `${agency.name.replace(/[^a-zA-Z0-9]+/g, '_')}_SOP_v${agency.version}_${projectName.replace(/[^a-zA-Z0-9]+/g, '_')}`.slice(0, 80);
      const payload = buildPdfPayloadFromTemplate({
        html, tokenValues: tokens,
        visualModel: { ...vm, refNo: tokens['{{ref_no}}'], issueDate, backgroundImageUrl: String(vm.backgroundImageUrl || project?.backgroundImageUrl || '') },
        companyLogoUrl: String((prefersBw && company?.logoBwUrl) || company?.logoUrl || ''),
        projectLogoUrl: String((prefersBw && project?.logoBwUrl) || project?.logoUrl || ''),
        companyAddress: String(company?.address || ''), companyMobile: String(company?.mobile || ''), companyEmail: String(company?.email || ''),
        companyFontFamily: String(company?.fontFamily || ''), companyFontUrl: String(company?.fontUrl || ''), companyFontFormat: String(company?.fontFormat || ''),
        gujaratiFontFamily: String(company?.gujaratiFontFamily || ''), gujaratiFontUrl: String(company?.gujaratiFontUrl || ''), gujaratiFontFormat: String(company?.gujaratiFontFormat || ''),
        paperSize: String(vm.paperSize || 'A4'), fileName,
      });
      const { record, bytes } = await generateSopPdf({ payload, fileName, agencyId: agency.id, projectId, projectName, version: agency.version, byName, uid, firm: values.en.agency_firm || '', values, rates: cleanRates });
      const blobBytes = new Uint8Array(bytes.length);
      blobBytes.set(bytes);
      const url = URL.createObjectURL(new Blob([blobBytes], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url; link.download = record.fileName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      onDone(template ? 'PDF downloaded and saved under PDFs made' : 'PDF downloaded with the default layout. Add an "Agency SOP" document template in Settings for your own letterhead');
    } catch (e: any) {
      setErr(e?.message || 'The PDF could not be made. Try again.');
    } finally { setBusy(''); }
  };

  const setRate = (i: number, patch: Partial<SopRateRow>) => setRates((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const filled = fields.filter((f) => (en[f.key] || '').trim()).length;

  return <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
    <DialogTitle sx={{ fontFamily: HEAD_FONT, fontWeight: 400, pb: 0.5 }}>Generate SOP PDF: {agency.id === COMMON_SOP_ID ? 'Common' : agency.name} v{agency.version}</DialogTitle>
    <DialogContent dividers>
      <Stack gap={2.25}>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          These details change from project to project. Anything left empty prints as a blank line ({'______'}) to fill in by hand. The PDF uses approved v{agency.version}, English with Gujarati below each point, on the project's letterhead.
        </Typography>
        <TextField select size="small" label="Project" value={projectId} onChange={(e) => setProjectId(String(e.target.value))} sx={{ maxWidth: 360 }}>
          {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        {filledFrom && <Alert severity="info" sx={{ py: 0 }}>{filledFrom}</Alert>}
        {projectId && (approved.length > 0 || openTasks.length > 0) && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 1.1, fontSize: 12.5 }}>
          {approved.length > 0
            ? <Typography sx={{ fontSize: 12.5 }}><b>Approved materials</b> from the start checklist will print: {approved.map((m) => `${m.label}: ${m.value}`).join('; ')}.</Typography>
            : <Typography sx={{ fontSize: 12.5, color: MUTED }}>No decisions filled in the start checklist yet, so no "Approved materials" table.</Typography>}
          {openTasks.length > 0 && <Typography sx={{ fontSize: 12.5, color: WARN, mt: 0.5 }}>{openTasks.length} start task{openTasks.length === 1 ? '' : 's'} not done for {projectName}: {openTasks.join('; ')}. You can still make the PDF.</Typography>}
        </Box>}
        {groups.map((g) => <Box key={g}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, mb: 1 }}>{g}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {fields.filter((f) => f.group === g).map((f) => <Stack key={f.key} gap={0.75} sx={{ gridColumn: f.kind === 'longtext' ? { sm: '1 / -1' } : undefined }}>
              <TextField size="small" label={f.label} value={en[f.key] || ''} onChange={(e) => setEn((v) => ({ ...v, [f.key]: e.target.value }))}
                type={f.kind === 'date' ? 'date' : f.kind === 'number' ? 'number' : 'text'} multiline={f.kind === 'longtext'} minRows={f.kind === 'longtext' ? 2 : undefined}
                placeholder={`e.g. ${sampleFor(f.key, agency, common).en}`} InputLabelProps={f.kind === 'date' ? { shrink: true } : undefined} fullWidth />
              {f.translate && <TextField size="small" label={`${f.labelGu} (ગુજરાતી)`} value={gu[f.key] || ''} onChange={(e) => setGu((v) => ({ ...v, [f.key]: e.target.value }))}
                multiline={f.kind === 'longtext'} placeholder={`e.g. ${sampleFor(f.key, agency, common).gu}`} helperText="Leave empty to print the English" fullWidth
                InputLabelProps={{ sx: { fontFamily: GU_FONT } }} inputProps={{ lang: 'gu', style: { fontFamily: GU_FONT } }} />}
            </Stack>)}
          </Box>
        </Box>)}
        <Box>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Rates (annexure)</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => setRates((r) => [...r, { item: '', unit: '', rate: '' }])}>Add row</Button>
          </Stack>
          <Stack gap={1}>
            {rates.map((r, i) => <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 2fr 1fr 1fr auto' }, gap: 1, alignItems: 'center' }}>
              <TextField size="small" label="Item" value={r.item} onChange={(e) => setRate(i, { item: e.target.value })} placeholder="e.g. Concealed CPVC point" />
              <TextField size="small" label="આઇટમ (ગુજરાતી)" value={r.itemGu || ''} onChange={(e) => setRate(i, { itemGu: e.target.value })} inputProps={{ lang: 'gu', style: { fontFamily: GU_FONT } }} InputLabelProps={{ sx: { fontFamily: GU_FONT } }} />
              <TextField size="small" label="Unit" value={r.unit} onChange={(e) => setRate(i, { unit: e.target.value })} placeholder="per point" />
              <TextField size="small" label="Rate ₹" value={r.rate} onChange={(e) => setRate(i, { rate: e.target.value })} />
              <IconAction title="Remove row" danger onClick={() => setRates((prev) => prev.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconAction>
            </Box>)}
            {!rates.length && <Typography sx={{ fontSize: 12.5, color: MUTED }}>No rates. The annexure prints an empty table to fill in by hand.</Typography>}
          </Stack>
        </Box>
        {err && <Alert severity="warning" onClose={() => setErr('')}>{err}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 1.75, gap: 1, flexWrap: 'wrap' }}>
      <Typography sx={{ fontSize: 12, color: MUTED, mr: 'auto' }}>{filled} of {fields.length} details filled</Typography>
      <AiButton outlined busy={busy === 'tr'} label="Fill Gujarati with AI" onClick={translateAll} />
      <Button sx={{ color: MUTED }} disabled={!!busy} onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={!!busy || !projectId} startIcon={busy === 'pdf' ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfOutlinedIcon />} onClick={generate}>
        {busy === 'pdf' ? 'Making PDF…' : 'Generate PDF'}
      </Button>
    </DialogActions>
  </Dialog>;
}

function trimAll(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(values).forEach(([k, v]) => { if (String(v || '').trim()) out[k] = String(v).trim(); });
  return out;
}
